import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { instructorApi, licenseApi, settingsApi, designApi, authApi, emailApi, bookingApi, extractApiError, getBackendUrl } from '../../lib/api';
import { renewalApi, dropboxApi, trainingApi } from '../../lib/api';
import api from '../../lib/api';
import PdfDropzone from '../common/PdfDropzone';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
    ChevronLeft, ChevronRight, Edit, Archive, RotateCcw, Trash2,
    Loader2, Mail, Phone, MapPin, Calendar, Award, BookOpen, ArrowLeft,
    Plus, X, RefreshCw, ChevronDown, ChevronUp, History, Download, FileText, Shield, CloudUpload, Check, Ban, Eye, Pencil, Save, HeartPulse, Undo2, User, CheckCircle2, XCircle, ThumbsUp, ThumbsDown, AlertTriangle
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { MemberMailHistory } from '../common/MemberMailHistory';

export function InstructorProfilePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    const [instructor, setInstructor] = useState(null);
    const [licenses, setLicenses] = useState([]);
    const [neighbors, setNeighbors] = useState({ prev: null, next: null });
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDropbox, setDeleteDropbox] = useState(true);
    const [wipeDialogOpen, setWipeDialogOpen] = useState(false);
    const [wipeDropbox, setWipeDropbox] = useState(false);
    const [wipeRunning, setWipeRunning] = useState(false);
    const [qualificationOptions, setQualificationOptions] = useState([]);
    const [licenseTypeOptions, setLicenseTypeOptions] = useState([]);
    const [licenseTypeItems, setLicenseTypeItems] = useState([]);
    const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
    const [editingLicense, setEditingLicense] = useState(null);
    const [licenseForm, setLicenseForm] = useState({
        license_type: '', license_number: '', issued_date: '', original_issued_date: '', valid_until: '', notes: ''
    });
    const [autoNumber, setAutoNumber] = useState(true);
    const [savingLicense, setSavingLicense] = useState(false);
    const [licensePdfFile, setLicensePdfFile] = useState(null);
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [deactivatingLicenseId, setDeactivatingLicenseId] = useState(null);
    const [deactivationReason, setDeactivationReason] = useState('');
    const [renewDialogOpen, setRenewDialogOpen] = useState(false);
    const [renewingLicense, setRenewingLicense] = useState(null);
    const [renewForm, setRenewForm] = useState({ valid_until: '', notes: '' });
    const [renewYears, setRenewYears] = useState(2);
    const [renewExceptionNotes, setRenewExceptionNotes] = useState('');
    const [renewSelectedRecords, setRenewSelectedRecords] = useState({});
    const [savingRenew, setSavingRenew] = useState(false);
    const [showHistory, setShowHistory] = useState({});
    const [btnColors, setBtnColors] = useState({ btn_edit: '#1F2937', btn_renew: '#2563EB', btn_danger: '#DC2626', btn_primary: '#CC0000', btn_edit_text: '#FFFFFF', btn_renew_text: '#FFFFFF', btn_danger_text: '#FFFFFF', btn_primary_text: '#FFFFFF' });

    const [fullProfile, setFullProfile] = useState(null);
    const [renewalRulesGlobal, setRenewalRulesGlobal] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [instrRes, neighborsRes, licensesRes, qualRes, ltypesRes, rulesRes] = await Promise.all([
                instructorApi.getOne(id),
                instructorApi.getNeighbors(id),
                licenseApi.getByInstructor(id),
                settingsApi.getQualifications(),
                settingsApi.getLicenseTypes(),
                settingsApi.getRenewalRulesPublic().catch(() => null),
            ]);
            if (rulesRes?.data) setRenewalRulesGlobal(rulesRes.data);
            setInstructor(instrRes.data);
            setNeighbors(neighborsRes.data);
            setLicenses(licensesRes.data);
            // Normalise qualification_licenses for the multi-select: accept both
            // the canonical array AND the legacy `is_bgpr_herzgruppenleiter` flag.
            const raw = instrRes.data || {};
            const qualLic = Array.isArray(raw.qualification_licenses) ? [...raw.qualification_licenses] : [];
            if (raw.is_bgpr_herzgruppenleiter && !qualLic.includes('BGPR-Herzgruppenleiter')) {
                qualLic.push('BGPR-Herzgruppenleiter');
            }
            // qualifications: legacy string → array; missing → []
            let qualifications = raw.qualifications;
            if (Array.isArray(qualifications)) {
                qualifications = qualifications.filter(Boolean);
            } else if (typeof qualifications === 'string' && qualifications.trim()) {
                qualifications = [qualifications.trim()];
            } else {
                qualifications = [];
            }
            setFormData({ ...raw, qualifications, qualification_licenses: qualLic });
            setQualificationOptions(qualRes.data.options || []);
            setLicenseTypeOptions(ltypesRes.data.options || []);
            setLicenseTypeItems(ltypesRes.data.items || []);

            // Load full profile (training records, bookings, etc.)
            try {
                const fpRes = await instructorApi.getFullProfile(id);
                setFullProfile(fpRes.data);
            } catch {
                // full profile may fail if no user_id linked
            }
        } catch (error) {
            toast.error('Übungsleiter nicht gefunden');
            navigate('/admin/instructors');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    // Lizenztypen und Design-Farben separat laden
    const loadLicenseTypes = useCallback(async () => {
        try {
            const [ltRes, designRes] = await Promise.all([
                settingsApi.getLicenseTypes(),
                designApi.get().catch(() => null),
            ]);
            const opts = ltRes.data?.options || ltRes.data || [];
            if (Array.isArray(opts) && opts.length > 0) {
                setLicenseTypeOptions(opts);
            }
            if (designRes?.data?.colors) {
                const c = designRes.data.colors;
                setBtnColors(prev => ({
                    btn_edit: c.btn_edit || prev.btn_edit,
                    btn_renew: c.btn_renew || prev.btn_renew,
                    btn_danger: c.btn_danger || prev.btn_danger,
                    btn_primary: c.btn_primary || prev.btn_primary,
                    btn_edit_text: c.btn_edit_text || prev.btn_edit_text,
                    btn_renew_text: c.btn_renew_text || prev.btn_renew_text,
                    btn_danger_text: c.btn_danger_text || prev.btn_danger_text,
                    btn_primary_text: c.btn_primary_text || prev.btn_primary_text,
                }));
            }
        } catch (error) {
            console.warn('Einstellungen konnten nicht geladen werden', error);
        }
    }, []);

    useEffect(() => {
        loadData();
        loadLicenseTypes();
        setEditMode(false);
    }, [loadData, loadLicenseTypes]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { title, first_name, last_name, salutation, email, phone, street, address, city, postal_code, country, club, birth_date, qualifications, qualification_licenses, is_bgpr_herzgruppenleiter, notes } = formData;
            const licArr = Array.isArray(qualification_licenses) ? qualification_licenses : [];
            const qualArr = Array.isArray(qualifications)
                ? qualifications.filter(Boolean)
                : (qualifications ? [qualifications] : []);
            const payload = {
                title, first_name, last_name, salutation, email, phone,
                street: street ?? address ?? '',
                city, postal_code, country, club, birth_date,
                qualifications: qualArr,
                qualification_licenses: licArr,
                is_bgpr_herzgruppenleiter: licArr.includes('BGPR-Herzgruppenleiter') || !!is_bgpr_herzgruppenleiter,
                notes,
            };
            await instructorApi.update(id, payload);
            toast.success('Änderungen gespeichert');
            setEditMode(false);
            loadData();
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async () => {
        try {
            await instructorApi.archive(id);
            toast.success('Übungsleiter archiviert');
            navigate('/admin/instructors');
        } catch (error) {
            toast.error('Fehler beim Archivieren');
        }
    };

    const handleRestore = async () => {
        try {
            await instructorApi.restore(id);
            toast.success('Übungsleiter wiederhergestellt');
            loadData();
        } catch (error) {
            toast.error('Fehler beim Wiederherstellen');
        }
    };

    const handleDelete = async () => {
        try {
            const res = await instructorApi.delete(id, deleteDropbox);
            const dbx = res?.data?.dropbox?.deleted ? ' · Dropbox-Ordner verschoben' : '';
            toast.success(`Übungsleiter gelöscht${dbx}`);
            navigate('/admin/instructors');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleWipeHistory = async () => {
        setWipeRunning(true);
        try {
            const res = await instructorApi.wipeHistory(id, wipeDropbox);
            const d = res.data?.deleted || {};
            const summary = Object.entries(d).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ') || 'keine Datensätze';
            const dbx = res.data?.dropbox?.deleted ? ' · Dropbox-Ordner verschoben' : '';
            toast.success(`Historie zurückgesetzt: ${summary}${dbx}`);
            setWipeDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen der Historie');
        } finally {
            setWipeRunning(false);
        }
    };

    const openCreateLicense = async () => {
        setEditingLicense(null);
        setLicenseForm({ license_type: '', license_number: '', issued_date: '', original_issued_date: '', valid_until: '', notes: '' });
        setAutoNumber(true);
        // Lizenztypen frisch laden beim Dialog-Öffnen
        try {
            const res = await settingsApi.getLicenseTypes();
            const opts = res.data?.options || res.data || [];
            if (Array.isArray(opts) && opts.length > 0) {
                setLicenseTypeOptions(opts);
            }
        } catch (e) { /* stille Fehlerbehandlung - vorhandene Optionen bleiben */ }
        setLicenseDialogOpen(true);
    };

    const openEditLicense = async (lic) => {
        setEditingLicense(lic);
        setLicenseForm({
            license_type: lic.license_type || '',
            license_number: lic.license_number || '',
            issued_date: lic.issued_date || '',
            original_issued_date: lic.original_issued_date || '',
            valid_until: lic.valid_until || '',
            notes: lic.notes || '',
        });
        setAutoNumber(false);
        // Lizenztypen frisch laden beim Dialog-Öffnen
        try {
            const res = await settingsApi.getLicenseTypes();
            const opts = res.data?.options || res.data || [];
            if (Array.isArray(opts) && opts.length > 0) {
                setLicenseTypeOptions(opts);
            }
        } catch (e) { /* stille Fehlerbehandlung */ }
        setLicenseDialogOpen(true);
    };

    const handleLicenseSubmit = async (e) => {
        e.preventDefault();
        if (licensePdfFile && !licensePdfFile.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Bitte nur PDF-Dateien hochladen');
            return;
        }
        setSavingLicense(true);
        try {
            let licenseId = editingLicense?.id;
            // Wenn Name in verwalteten Lizenzarten existiert, ID mitsenden — dann wählt der
            // PDF-Renderer automatisch die verknüpfte Lizenz-Vorlage.
            const matched = (licenseTypeItems || []).find(it =>
                !it.archived && String(it.name || '').trim().toLowerCase() === String(licenseForm.license_type || '').trim().toLowerCase()
            );
            const licenseTypeId = matched?.id || null;
            if (editingLicense) {
                await licenseApi.update(editingLicense.id, {
                    license_type: licenseForm.license_type,
                    license_type_id: licenseTypeId,
                    license_number: licenseForm.license_number,
                    issued_date: licenseForm.issued_date,
                    original_issued_date: licenseForm.original_issued_date,
                    valid_until: licenseForm.valid_until,
                    notes: licenseForm.notes,
                });
                toast.success('Lizenz aktualisiert');
            } else {
                const res = await licenseApi.create({
                    instructor_id: id,
                    license_type: licenseForm.license_type,
                    license_type_id: licenseTypeId,
                    license_number: autoNumber ? '' : licenseForm.license_number,
                    issued_date: licenseForm.issued_date,
                    original_issued_date: licenseForm.original_issued_date,
                    valid_until: licenseForm.valid_until,
                    notes: licenseForm.notes,
                });
                licenseId = res?.data?.id || licenseId;
                toast.success('Lizenz erstellt');
            }
            // Optionaler PDF-Upload nach Dropbox (nur bei Neuanlage / wenn Datei gewählt)
            if (licensePdfFile && licenseId) {
                try {
                    const upRes = await dropboxApi.uploadLicense(licenseId, licensePdfFile);
                    const path = upRes?.data?.dropbox_path || 'Dropbox';
                    toast.success(`Lizenz-PDF hochgeladen: ${path}`);
                } catch (uploadErr) {
                    const detail = uploadErr.response?.data?.detail
                        || uploadErr.message
                        || 'Unbekannter Fehler';
                    // eslint-disable-next-line no-console
                    console.error('Dropbox-Upload fehlgeschlagen', uploadErr);
                    toast.error(`Dropbox-Upload fehlgeschlagen: ${detail} — Lizenz wurde gespeichert, PDF nicht. Bitte erneut versuchen.`, { duration: 8000 });
                }
            }
            setLicensePdfFile(null);
            setLicenseDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSavingLicense(false);
        }
    };

    const handleDeactivateLicense = async () => {
        if (!deactivatingLicenseId) return;
        try {
            await licenseApi.deactivate(deactivatingLicenseId, deactivationReason);
            toast.success('Lizenz deaktiviert');
            setDeactivateDialogOpen(false);
            setDeactivatingLicenseId(null);
            setDeactivationReason('');
            loadData();
        } catch (error) {
            toast.error('Fehler beim Deaktivieren');
        }
    };

    const handleActivateLicense = async (licenseId) => {
        try {
            await licenseApi.activate(licenseId);
            toast.success('Lizenz aktiviert');
            loadData();
        } catch (error) {
            toast.error('Fehler beim Aktivieren');
        }
    };

    const handleLicenseToggle = (lic, checked) => {
        if (!checked) {
            setDeactivatingLicenseId(lic.id);
            setDeactivationReason('');
            setDeactivateDialogOpen(true);
        } else {
            handleActivateLicense(lic.id);
        }
    };

    const openRenewDialog = (lic) => {
        setRenewingLicense(lic);
        // Bezugsjahr = letztes gültig-bis der zu verlängernden Lizenz
        const defYears = renewalRulesGlobal?.default_renewal_years || 2;
        const baseYear = lic?.valid_until
            ? parseInt(String(lic.valid_until).slice(0, 4), 10)
            : new Date().getFullYear();
        setRenewYears(defYears);
        setRenewExceptionNotes('');
        setRenewForm({ valid_until: `${baseYear + defYears}-12-31`, notes: '' });

        // Anrechenbare Nachweise ermitteln & vorselektieren.
        // WICHTIG (Bug Teil 55): nur bereits geprüfte („approved") Records auto-selektieren.
        // „Zu prüfen"-Uploads dürfen erst nach Prüfung durch die Geschäftsstelle
        // für eine Verlängerung angerechnet werden.
        const cutoff = lic?.issued_date || '';
        const selected = {};
        (fullProfile?.training_records || []).forEach(r => {
            if (r.record_type === 'first_aid') return;
            if (r.used_for_renewal_id || r.used_for_license_id) return;
            if ((r.review_status || 'submitted') !== 'approved') return;
            if (cutoff && (r.date_issued || '') < cutoff) return;
            selected[r.id] = true;
        });
        setRenewSelectedRecords(selected);
        setRenewDialogOpen(true);
    };

    const handleRenewLicense = async (e) => {
        e.preventDefault();
        if (!renewingLicense) return;
        if (renewYears === 1 && !renewExceptionNotes.trim()) {
            toast.error('Bei 1-Jahres-Verlängerung bitte eine Begründung angeben.');
            return;
        }
        setSavingRenew(true);
        try {
            const consumed = Object.entries(renewSelectedRecords)
                .filter(([, v]) => v).map(([k]) => k);
            await licenseApi.renew(renewingLicense.id, {
                valid_until: renewForm.valid_until,
                notes: renewForm.notes,
                renewal_years: renewYears,
                exception_notes: renewYears === 1 ? renewExceptionNotes.trim() : '',
                consumed_record_ids: consumed,
            });
            toast.success('Lizenz verlängert');
            setRenewDialogOpen(false);
            setRenewingLicense(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Verlängern');
        } finally {
            setSavingRenew(false);
        }
    };

    // Bestandslizenz vom Mitglied anerkennen oder ablehnen.
    // - Anerkennen: setzt verified_by_admin=True (auch bei abgelaufener Lizenz möglich)
    // - Ablehnen: löscht die Lizenz, Mitglied bekommt Begründungs-Mail
    const handleVerifyLicense = async (lic, reject) => {
        if (reject) {
            const reason = window.prompt(
                `Bestandslizenz Nr. ${lic.license_number || '(ohne Nummer)'} ablehnen.\n\n`
                + `Bitte gib eine kurze Begründung an — sie wird dem Mitglied per Mail mitgeteilt:`,
                '',
            );
            if (reason === null) return; // abgebrochen
            try {
                const res = await api.post(`/licenses/${lic.id}/verify`, { reject: true, reason });
                toast.success(res.data?.message || 'Lizenz abgelehnt');
                loadData();
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Ablehnung fehlgeschlagen');
            }
            return;
        }
        const expiredNote = lic.valid_until && new Date(lic.valid_until) < new Date()
            ? '\n\nHinweis: Diese Lizenz ist bereits abgelaufen. Sie wird trotzdem anerkannt und steht damit für eine Verlängerung zur Verfügung.'
            : '';
        if (!window.confirm(`Bestandslizenz Nr. ${lic.license_number || '(ohne Nummer)'} anerkennen?${expiredNote}`)) return;
        try {
            const res = await api.post(`/licenses/${lic.id}/verify`, { reject: false });
            toast.success(res.data?.message || 'Lizenz anerkannt');
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Anerkennung fehlgeschlagen');
        }
    };

    // Macht eine fälschlich erzeugte Verlängerungs-Lizenz rückgängig: löscht
    // die neue Lizenz, reaktiviert die alte, gibt verbrauchte Fortbildungs-
    // Nachweise frei und setzt einen verknüpften Antrag zurück auf „pending".
    const handleUndoRenewal = async (lic) => {
        const msg = `⚠ Verlängerung rückgängig machen?\n\n`
            + `Lizenz Nr. ${lic.license_number} (gültig bis ${formatDateDE(lic.valid_until)}) wird gelöscht.\n`
            + `Die alte Lizenz wird wieder aktiviert. Verbrauchte Fortbildungs-Nachweise werden freigegeben.\n`
            + `Ein verknüpfter Verlängerungs-Antrag wird zurück auf „In Bearbeitung" gesetzt, damit er regulär geprüft werden kann.\n\n`
            + `Dieser Schritt ist nicht automatisch umkehrbar.`;
        if (!window.confirm(msg)) return;
        try {
            const res = await api.post(`/licenses/${lic.id}/undo-renewal`);
            const d = res.data || {};
            toast.success(
                `Verlängerung rückgängig gemacht · ${d.training_records_released || 0} Nachweis(e) freigegeben`
                + (d.renewal_reset_to_pending ? ' · Antrag erneut prüfbar' : '')
            );
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rückgängig fehlgeschlagen');
        }
    };

    const handleDownloadPdf = async (lic) => {
        // 2. otherwise → generated from active template, archived to Dropbox,
        //    then served (also sets dropbox_path for next time).
        try {
            toast.info(lic.dropbox_path ? 'PDF wird aus Dropbox geladen...' : 'PDF wird erzeugt und archiviert...');
            const res = await licenseApi.getPdf(lic.id);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            const fallbackName = `${instructor.last_name}_${lic.license_number || lic.id}.pdf`;
            a.download = (lic.dropbox_path && lic.dropbox_path.split('/').pop()) || fallbackName;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('PDF heruntergeladen');
            // Refresh so the freshly archived dropbox_path appears on the card.
            if (!lic.dropbox_path) loadData();
        } catch (error) {
            toast.error(await extractApiError(error, 'Fehler beim Herunterladen / Generieren'));
        }
    };

    const handleDiagnoseLicensePdf = async () => {
        try {
            toast.info('Lizenz-PDF-Diagnose wird ausgeführt...');
            const res = await api.get('/system/license-pdf-diagnostics');
            const d = res.data;
            const issues = d.issues || [];
            const tpl = d.templates || {};
            const dbx = d.dropbox || {};
            const lines = [
                `Templates: ${tpl.license_total || 0} Lizenz-Vorlage(n), davon aktiv: ${tpl.license_active || 0}`,
                `Dropbox: ${dbx.connected ? `verbunden (${dbx.account_email || 'unbekannt'}, Basis-Ordner: ${dbx.base_folder || '/BGPR'})` : `NICHT verbunden – ${dbx.error || 'unbekannter Grund'}`}`,
            ];
            if ((tpl.license_list || []).length) {
                lines.push('Lizenz-Vorlagen im System:');
                tpl.license_list.forEach(t => lines.push(`  • ${t.name || '(unbenannt)'} – ${t.is_active ? 'AKTIV' : 'inaktiv'}`));
            }
            if (issues.length) {
                lines.push('Problem(e):');
                issues.forEach(i => lines.push(`  ✗ ${i}`));
            } else {
                lines.push('✓ Alles in Ordnung');
            }
            const message = lines.join('\n');
            // eslint-disable-next-line no-alert
            window.alert(message);
        } catch (error) {
            toast.error(await extractApiError(error, 'Diagnose fehlgeschlagen'));
        }
    };

    const handleDeleteLicense = async (lic) => {
        const label = lic.license_number || lic.license_type || 'Lizenz';
        if (!window.confirm(`Lizenz "${label}" wirklich unwiderruflich löschen?`)) return;
        try {
            await licenseApi.delete(lic.id);
            toast.success('Lizenz gelöscht');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const toggleHistory = (licenseNumber) => {
        setShowHistory(prev => ({ ...prev, [licenseNumber]: !prev[licenseNumber] }));
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    if (!instructor) return null;

    const initials = `${(instructor.first_name?.[0] || '').toUpperCase()}${(instructor.last_name?.[0] || '').toUpperCase()}`;

    return (
        <AdminLayout>
            {/* Breadcrumb & Navigation */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <button
                    onClick={() => navigate('/admin/instructors')}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="back-to-list"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Zurück zur Liste
                </button>

                {/* Prev/Next */}
                <div className="flex items-center gap-2" data-testid="instructor-nav">
                    {neighbors.prev ? (
                        <button
                            onClick={() => navigate(`/admin/instructors/${neighbors.prev.id}`)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-muted hover:bg-muted/80 transition-colors"
                            title={`${neighbors.prev.first_name} ${neighbors.prev.last_name}`}
                            data-testid="nav-prev"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">Vorheriger</span>
                        </button>
                    ) : (
                        <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-muted/50 text-muted-foreground cursor-not-allowed">
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">Vorheriger</span>
                        </span>
                    )}
                    {neighbors.next ? (
                        <button
                            onClick={() => navigate(`/admin/instructors/${neighbors.next.id}`)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-muted hover:bg-muted/80 transition-colors"
                            title={`${neighbors.next.first_name} ${neighbors.next.last_name}`}
                            data-testid="nav-next"
                        >
                            <span className="hidden sm:inline">Nächster</span>
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    ) : (
                        <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-muted/50 text-muted-foreground cursor-not-allowed">
                            <span className="hidden sm:inline">Nächster</span>
                            <ChevronRight className="h-4 w-4" />
                        </span>
                    )}
                </div>
            </div>

            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white text-xl font-bold shadow-md">
                        {initials}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold" data-testid="instructor-name">
                            {instructor.title ? `${instructor.title} ` : ''}{instructor.first_name} {instructor.last_name}
                        </h1>
                        <div className="flex items-center gap-3 mt-1">
                            {instructor.is_archived ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                    <Archive className="h-3 w-3" /> Archiviert
                                </span>
                            ) : instructor.is_active !== false ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    Aktiv
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    Inaktiv
                                </span>
                            )}
                            {instructor.member_since && (
                                <span className="text-sm text-muted-foreground">
                                    Mitglied seit {new Date(instructor.member_since).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2" data-testid="profile-actions">
                    {!editMode && (
                        <Button variant="default" onClick={() => setEditMode(true)} data-testid="edit-button">
                            <Edit className="mr-2 h-4 w-4" /> Bearbeiten
                        </Button>
                    )}
                    {instructor.is_archived ? (
                        <Button variant="outline" onClick={handleRestore} data-testid="restore-button">
                            <RotateCcw className="mr-2 h-4 w-4" /> Wiederherstellen
                        </Button>
                    ) : (
                        <Button variant="outline" onClick={handleArchive} data-testid="archive-button">
                            <Archive className="mr-2 h-4 w-4" /> Archivieren
                        </Button>
                    )}
                    {isAdmin() && (
                        <Button
                            variant="outline"
                            onClick={() => setWipeDialogOpen(true)}
                            className="border-amber-400 text-amber-900 hover:bg-amber-50"
                            data-testid="wipe-history-button"
                            title="Lizenzen, Buchungen, Rechnungen etc. löschen — Mitglied bleibt erhalten"
                        >
                            <Trash2 className="mr-2 h-4 w-4" /> Historie zurücksetzen
                        </Button>
                    )}
                    {isAdmin() && (
                        <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-button">
                            <Trash2 className="mr-2 h-4 w-4" /> Löschen
                        </Button>
                    )}
                </div>
            </div>

            {editMode ? (
                /* Edit Mode */
                <form onSubmit={handleSave}>
                    <div className="grid lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="rounded-lg border bg-white p-6">
                                <h2 className="text-lg font-semibold mb-4">Persönliche Daten</h2>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Titel</Label>
                                        <Select value={formData.title || '_none'} onValueChange={v => setFormData(p => ({...p, title: v === '_none' ? '' : v}))}>
                                            <SelectTrigger className="h-9 text-sm" data-testid="edit-title">
                                                <SelectValue placeholder="Kein Titel" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="_none">Kein Titel</SelectItem>
                                                <SelectItem value="Dr.">Dr.</SelectItem>
                                                <SelectItem value="Prof.">Prof.</SelectItem>
                                                <SelectItem value="Prof. Dr.">Prof. Dr.</SelectItem>
                                                <SelectItem value="PD Dr.">PD Dr.</SelectItem>
                                                <SelectItem value="Dipl.">Dipl.</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Vorname *</Label>
                                        <Input name="first_name" value={formData.first_name || ''} onChange={handleChange} required data-testid="edit-firstname" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Nachname *</Label>
                                        <Input name="last_name" value={formData.last_name || ''} onChange={handleChange} required data-testid="edit-lastname" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>E-Mail *</Label>
                                        <Input name="email" type="email" value={formData.email || ''} onChange={handleChange} required data-testid="edit-email" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Telefon</Label>
                                        <Input name="phone" value={formData.phone || ''} onChange={handleChange} data-testid="edit-phone" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Geburtsdatum</Label>
                                        <Input name="birth_date" type="date" value={formData.birth_date || ''} onChange={handleChange} data-testid="edit-birthdate" />
                                    </div>
                                    <div className="sm:col-span-2 space-y-2">
                                        <Label>Qualifikation (Mehrfachauswahl)</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Pflegbar unter Einstellungen → System → Berufliche Vorqualifikation.
                                        </p>
                                        {qualificationOptions.length === 0 ? (
                                            <p className="text-sm text-gray-500">Keine Qualifikationen in den Einstellungen definiert.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="qualifications-multi">
                                                {qualificationOptions.map((q) => {
                                                    const selected = (formData.qualifications || []).includes(q);
                                                    return (
                                                        <label key={q} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selected ? 'bg-primary/5 border-primary/40' : 'bg-white hover:bg-gray-50'}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selected}
                                                                onChange={(e) => {
                                                                    const prev = formData.qualifications || [];
                                                                    const next = e.target.checked
                                                                        ? [...prev, q]
                                                                        : prev.filter((x) => x !== q);
                                                                    setFormData({ ...formData, qualifications: next });
                                                                }}
                                                                className="w-5 h-5 rounded text-primary"
                                                                data-testid={`edit-qualification-${q}`}
                                                            />
                                                            <span className="text-sm font-medium">{q}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t">
                                    <Label className="mb-2 block">Lizenz-/Qualifikationstypen</Label>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        Mehrfachauswahl möglich. Pflegbar unter
                                        Einstellungen → System → Lizenztypen.
                                    </p>
                                    {licenseTypeOptions.length === 0 ? (
                                        <p className="text-sm text-gray-500">Keine Lizenztypen in den Einstellungen definiert.</p>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="qualification-licenses">
                                            {licenseTypeOptions.map((lt) => {
                                                const selected = (formData.qualification_licenses || []).includes(lt);
                                                return (
                                                    <label key={lt} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selected ? 'bg-primary/5 border-primary/40' : 'bg-white hover:bg-gray-50'}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={(e) => {
                                                                const prev = formData.qualification_licenses || [];
                                                                const next = e.target.checked
                                                                    ? [...prev, lt]
                                                                    : prev.filter((x) => x !== lt);
                                                                setFormData({
                                                                    ...formData,
                                                                    qualification_licenses: next,
                                                                    // keep legacy flag in sync for any old UI still reading it
                                                                    is_bgpr_herzgruppenleiter: next.includes('BGPR-Herzgruppenleiter'),
                                                                });
                                                            }}
                                                            className="w-5 h-5 rounded text-primary"
                                                            data-testid={`edit-license-type-${lt}`}
                                                        />
                                                        <span className="text-sm font-medium">{lt}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="rounded-lg border bg-white p-6">
                                <h2 className="text-lg font-semibold mb-4">Adresse</h2>
                                <div className="grid sm:grid-cols-3 gap-4">
                                    <div className="sm:col-span-3 space-y-2">
                                        <Label>Straße & Hausnummer</Label>
                                        <Input name="street" value={formData.street ?? formData.address ?? ''} onChange={handleChange} data-testid="edit-street" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>PLZ</Label>
                                        <Input name="postal_code" value={formData.postal_code || ''} onChange={handleChange} data-testid="edit-postal" />
                                    </div>
                                    <div className="sm:col-span-2 space-y-2">
                                        <Label>Ort</Label>
                                        <Input name="city" value={formData.city || ''} onChange={handleChange} data-testid="edit-city" />
                                    </div>
                                    <div className="sm:col-span-3 space-y-2">
                                        <Label>Verein</Label>
                                        <Input name="club" value={formData.club || ''} onChange={handleChange} placeholder="z.B. SG Herz Berlin e.V." data-testid="edit-club" />
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-lg border bg-white p-6">
                                <h2 className="text-lg font-semibold mb-4">Notizen</h2>
                                <Textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={4} placeholder="Interne Notizen..." data-testid="edit-notes" />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <Button type="submit" className="w-full" disabled={saving} data-testid="save-button">
                                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Speichern
                            </Button>
                            <Button type="button" variant="outline" className="w-full" onClick={() => { setEditMode(false); setFormData(instructor); }} data-testid="cancel-edit">
                                Abbrechen
                            </Button>
                        </div>
                    </div>
                </form>
            ) : (
                /* View Mode */
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 flex flex-col gap-6">
                        {/* Contact */}
                        <div className="rounded-lg border bg-white p-6">
                            <h2 className="text-lg font-semibold mb-4">Kontaktdaten</h2>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <InfoItem icon={Mail} label="E-Mail" value={instructor.email} isLink={`mailto:${instructor.email}`} />
                                <InfoItem icon={Phone} label="Telefon" value={instructor.phone} isLink={instructor.phone ? `tel:${instructor.phone}` : null} />
                                <InfoItem icon={Calendar} label="Geburtsdatum" value={instructor.birth_date ? new Date(instructor.birth_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null} />
                                <InfoItem icon={MapPin} label="Adresse" value={
                                    (instructor.street || instructor.address || instructor.city) ?
                                        `${instructor.street || instructor.address || ''}\n${instructor.postal_code || instructor.zip || ''} ${instructor.city || ''}`.trim() : null
                                } />
                                <InfoItem icon={Shield} label="Verein" value={instructor.club} />
                            </div>
                        </div>

                        {/* Qualifications */}
                        {(() => {
                            const qLic = Array.isArray(instructor.qualification_licenses) ? instructor.qualification_licenses : [];
                            // qualifications kann String (legacy) oder Array sein
                            let qArr = instructor.qualifications;
                            if (Array.isArray(qArr)) qArr = qArr.filter(Boolean);
                            else if (typeof qArr === 'string' && qArr.trim()) qArr = [qArr.trim()];
                            else qArr = [];
                            const showLegacyHGL = instructor.is_bgpr_herzgruppenleiter && !qLic.includes('BGPR-Herzgruppenleiter');
                            if (qArr.length === 0 && qLic.length === 0 && !showLegacyHGL) return null;
                            return (
                                <div className="rounded-lg border bg-white p-6">
                                    <h2 className="text-lg font-semibold mb-4">Qualifikation</h2>
                                    <div className="flex flex-wrap gap-2">
                                        {qArr.map((q) => (
                                            <span key={`q-${q}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                                <Award className="h-3.5 w-3.5" />
                                                {q}
                                            </span>
                                        ))}
                                        {qLic.map((lt) => (
                                            <span key={`l-${lt}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                                                <Award className="h-3.5 w-3.5" />
                                                {lt}
                                            </span>
                                        ))}
                                        {showLegacyHGL && (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                                                <Award className="h-3.5 w-3.5" />
                                                BGPR-Herzgruppenleiter
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Notes */}
                        {instructor.notes && (
                            <div className="rounded-lg border bg-white p-6">
                                <h2 className="text-lg font-semibold mb-4">Notizen</h2>
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{instructor.notes}</p>
                            </div>
                        )}

                        {/* Licenses */}
                        <div className="rounded-lg border bg-white p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold">Lizenzen</h2>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={handleDiagnoseLicensePdf} title="Prüft, ob Lizenzvorlage aktiv und Dropbox verbunden ist" data-testid="diagnose-license-pdf">
                                        Diagnose
                                    </Button>
                                    <Button size="sm" onClick={openCreateLicense} data-testid="add-license-button">
                                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                                        Lizenz hinzufügen
                                    </Button>
                                </div>
                            </div>
                            {licenses.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Keine Lizenzen vorhanden</p>
                            ) : (
                                <LicenseList
                                    licenses={licenses}
                                    onEdit={openEditLicense}
                                    onToggle={handleLicenseToggle}
                                    onRenew={openRenewDialog}
                                    onDownloadPdf={handleDownloadPdf}
                                    onDelete={isAdmin() ? handleDeleteLicense : null}
                                    onUndoRenewal={isAdmin() ? handleUndoRenewal : null}
                                    onVerify={isAdmin() ? handleVerifyLicense : null}
                                    showHistory={showHistory}
                                    toggleHistory={toggleHistory}
                                    btnColors={btnColors}
                                    trainingRecords={fullProfile?.training_records || []}
                                    renewals={fullProfile?.renewal_requests || []}
                                    renewalRules={renewalRulesGlobal}
                                />
                            )}
                        </div>

                        {/* Lizenzverlängerungsanträge (direkt nach Lizenzen) */}
                        {fullProfile?.renewal_requests?.length > 0 && (
                            <RenewalRequestsSection renewals={fullProfile.renewal_requests} onReload={loadData} />
                        )}

                        {/* Fortbildungsnachweise (from linked user) */}
                        {fullProfile?.training_records?.length > 0 && (
                            <div className="rounded-lg border bg-white p-6" data-testid="training-records-section">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold flex items-center gap-2">
                                        <FileText className="h-5 w-5 text-muted-foreground" />
                                        Fortbildungsnachweise
                                    </h2>
                                    {/* Cleanup-Werkzeug: setzt fälschlich auf „anerkannt" stehende
                                        User-Uploads zurück auf „In Prüfung". Schont auto_created (BGPR-Seminar). */}
                                    {isAdmin() && (() => {
                                        const wronglyApproved = (fullProfile.training_records || []).filter(r =>
                                            (r.review_status || 'submitted') === 'approved'
                                            && r.auto_created !== true
                                            && !r.used_for_renewal_id
                                            && !r.used_for_license_id
                                        );
                                        if (wronglyApproved.length === 0) return null;
                                        return (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                                onClick={async () => {
                                                    if (!window.confirm(`${wronglyApproved.length} Nachweis(e) wieder auf „In Prüfung" zurücksetzen?\nBGPR-Seminar-Records und bereits verbrauchte Nachweise bleiben unberührt.`)) return;
                                                    try {
                                                        const res = await api.post(`/training-records/admin/reset-review/${fullProfile.id}`);
                                                        toast.success(res.data?.message || 'Zurückgesetzt');
                                                        loadData();
                                                    } catch (e) {
                                                        toast.error(e.response?.data?.detail || 'Reset fehlgeschlagen');
                                                    }
                                                }}
                                                data-testid="reset-review-status-btn"
                                                title='Setzt User-Uploads, die fälschlich auf „anerkannt" stehen, auf „In Prüfung" zurück (Cleanup für Legacy-Daten)'
                                            >
                                                {wronglyApproved.length} Nachweis(e) erneut prüfen
                                            </Button>
                                        );
                                    })()}
                                </div>
                                <div className="space-y-2">
                                    {fullProfile.training_records.map((r) => (
                                        <TrainingRecordRow key={r.id} record={r} onReload={loadData} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Buchungen (from linked user) */}
                        {fullProfile?.bookings?.length > 0 && (
                            <div className="rounded-lg border bg-white p-6" data-testid="bookings-section">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <BookOpen className="h-5 w-5 text-muted-foreground" />
                                    Seminar-Buchungen
                                </h2>
                                <div className="space-y-2">
                                    {fullProfile.bookings.map((b) => (
                                        <MemberBookingRow key={b.id} booking={b} onReload={loadData} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* E-Mail-Verlauf (Admin-Sicht – inkl. Newsletter, Bestätigungen, Lizenzen, …) */}
                        {isAdmin() && (
                            <MemberMailHistory
                                loader={(params) => emailApi.logsByUser(id, params)}
                                showError={true}
                                canResend={true}
                                title="E-Mail-Verlauf"
                                testIdPrefix="instructor-mail"
                            />
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        <div className="rounded-lg border bg-white p-6">
                            <h2 className="text-lg font-semibold mb-4">Schnellinfo</h2>
                            <div className="space-y-3">
                                <SidebarItem label="Status" value={
                                    instructor.is_archived ? 'Archiviert' :
                                    instructor.is_active !== false ? 'Aktiv' : 'Inaktiv'
                                } />
                                {(() => {
                                    if (!fullProfile?.training_records) return null;
                                    const regular = fullProfile.training_records.filter(r => r.record_type !== 'first_aid' && !r.used_for_renewal_id);
                                    const availableUE = regular.reduce((sum, r) => sum + (r.approved_ue != null ? r.approved_ue : r.ue_count || 0), 0);
                                    const usedRecords = fullProfile.training_records.filter(r => r.record_type !== 'first_aid' && r.used_for_renewal_id);
                                    const usedUE = usedRecords.reduce((sum, r) => sum + (r.approved_ue != null ? r.approved_ue : r.ue_count || 0), 0);
                                    return (
                                        <>
                                            <div className="py-1.5 border-b border-gray-100 last:border-0 px-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-muted-foreground">Verfügbare UE</span>
                                                    <span className="font-bold text-green-600">{availableUE} UE</span>
                                                </div>
                                            </div>
                                            {usedUE > 0 && (
                                                <div className="py-1.5 border-b border-gray-100 last:border-0 px-1">
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-muted-foreground">Bereits verwendet</span>
                                                        <span className="font-medium text-gray-400">{usedUE} UE</span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                                <SidebarItem label="Lizenzen" value={String(licenses.length)} />
                                {fullProfile?.training_records && (
                                    <SidebarItem label="Nachweise" value={String(fullProfile.training_records.length)} />
                                )}
                                {fullProfile?.bookings && (
                                    <SidebarItem label="Buchungen" value={String(fullProfile.bookings.length)} />
                                )}
                                {fullProfile?.user_profile && (
                                    <div className="py-1.5 border-b border-gray-100 px-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full text-xs h-8"
                                            onClick={async () => {
                                                try {
                                                    const res = await authApi.impersonate(fullProfile.user_profile.id);
                                                    const { token, user: u } = res.data;
                                            const url = `/portal/impersonate?token=${encodeURIComponent(token)}&user=${encodeURIComponent(JSON.stringify(u))}`;
                                                    window.open(url, '_blank');
                                                } catch (e) { toast.error('Fehler beim Öffnen des Portals'); }
                                            }}
                                            data-testid="impersonate-user-btn"
                                        >
                                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                                            Als Benutzer ansehen
                                        </Button>
                                    </div>
                                )}
                                <SidebarItem label="Erstellt am" value={instructor.created_at ? new Date(instructor.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Übungsleiter löschen?</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground">
                        <strong>{instructor.first_name} {instructor.last_name}</strong> und alle zugehörigen Daten
                        werden unwiderruflich gelöscht. Fortfahren?
                    </p>
                    <div className="flex items-start gap-2 mt-3">
                        <Checkbox
                            id="delete-dropbox"
                            checked={deleteDropbox}
                            onCheckedChange={setDeleteDropbox}
                            data-testid="delete-dropbox-checkbox"
                        />
                        <Label htmlFor="delete-dropbox" className="text-sm leading-snug cursor-pointer">
                            Dropbox-Ordner mitlöschen (DSGVO)
                            <span className="block text-xs text-muted-foreground mt-0.5">
                                Verschiebt alle PDFs des Mitglieds (Lizenzen, Bescheinigungen, Quittungen) in den Dropbox-Papierkorb. 30 Tage wiederherstellbar.
                            </span>
                        </Label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                        <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">Löschen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Wipe History Confirmation */}
            <Dialog open={wipeDialogOpen} onOpenChange={setWipeDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Historie wirklich zurücksetzen?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 text-sm text-muted-foreground">
                        <p>
                            Es werden <strong>alle Lizenzen, Verlängerungsanträge, Buchungen, Stornierungen,
                            Rechnungen, Training-Records und QR-Scans</strong> von <strong>{instructor.first_name} {instructor.last_name}</strong> unwiderruflich entfernt.
                        </p>
                        <p>
                            Das Akademie-Mitglied selbst sowie alle Seminare bleiben unverändert erhalten.
                        </p>
                        <p className="text-amber-700">
                            Nützlich, wenn die alten PDF-Links nach einem Object-Storage-Wechsel ins Leere zeigen.
                            Vorher unbedingt ein Backup ziehen!
                        </p>
                    </div>
                    <div className="flex items-start gap-2 mt-3">
                        <Checkbox
                            id="wipe-dropbox"
                            checked={wipeDropbox}
                            onCheckedChange={setWipeDropbox}
                            data-testid="wipe-dropbox-checkbox"
                        />
                        <Label htmlFor="wipe-dropbox" className="text-sm leading-snug cursor-pointer">
                            Auch Dropbox-Ordner mitlöschen
                            <span className="block text-xs text-muted-foreground mt-0.5">
                                Verschiebt alle PDFs (Lizenzen, Bescheinigungen, Quittungen) des Mitglieds in den Dropbox-Papierkorb (30 Tage wiederherstellbar).
                            </span>
                        </Label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setWipeDialogOpen(false)} disabled={wipeRunning}>Abbrechen</Button>
                        <Button
                            onClick={handleWipeHistory}
                            disabled={wipeRunning}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="confirm-wipe-history"
                        >
                            {wipeRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Historie zurücksetzen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* License Dialog */}
            <Dialog open={licenseDialogOpen} onOpenChange={(o) => { setLicenseDialogOpen(o); if (!o) setLicensePdfFile(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingLicense ? 'Lizenz bearbeiten' : 'Neue Lizenz'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleLicenseSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="lic_type">Lizenz *</Label>
                                <Select
                                    value={licenseForm.license_type}
                                    onValueChange={(val) => setLicenseForm({ ...licenseForm, license_type: val })}
                                    required
                                >
                                    <SelectTrigger data-testid="license-type-select">
                                        <SelectValue placeholder="-- Bitte wählen --" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {licenseTypeOptions.map((t) => (
                                            <SelectItem key={t} value={t}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="lic_number">Lizenznummer</Label>
                                {!editingLicense && (
                                    <div className="flex items-center gap-3 mb-2">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="radio"
                                                checked={autoNumber}
                                                onChange={() => setAutoNumber(true)}
                                                className="w-4 h-4 text-primary"
                                                data-testid="license-number-auto"
                                            />
                                            Automatisch vergeben
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="radio"
                                                checked={!autoNumber}
                                                onChange={() => setAutoNumber(false)}
                                                className="w-4 h-4 text-primary"
                                                data-testid="license-number-manual"
                                            />
                                            Manuell eingeben
                                        </label>
                                    </div>
                                )}
                                {(editingLicense || !autoNumber) ? (
                                    <Input
                                        id="lic_number"
                                        value={licenseForm.license_number}
                                        onChange={(e) => setLicenseForm({ ...licenseForm, license_number: e.target.value })}
                                        placeholder="z.B. BGPR-HGL-2026-001"
                                        required={!!editingLicense || !autoNumber}
                                        data-testid="license-number-input"
                                    />
                                ) : (
                                    <p className="text-sm text-muted-foreground px-3 py-2 bg-muted/50 rounded-md" data-testid="license-number-auto-hint">
                                        Wird automatisch vergeben (Format: BGPR-HGL-JJJJ-XXX)
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="lic_issued">Ausgestellt am</Label>
                                    <Input
                                        id="lic_issued"
                                        type="date"
                                        value={licenseForm.issued_date}
                                        onChange={(e) => setLicenseForm({ ...licenseForm, issued_date: e.target.value })}
                                        min="1900-01-01"
                                        max="2100-12-31"
                                        data-testid="license-issued-date"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lic_valid">Gültig bis *</Label>
                                    <Input
                                        id="lic_valid"
                                        type="date"
                                        value={licenseForm.valid_until}
                                        onChange={(e) => setLicenseForm({ ...licenseForm, valid_until: e.target.value })}
                                        required
                                        min="1900-01-01"
                                        max="2100-12-31"
                                        data-testid="license-valid-until"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="lic_original_issued">Erstausstellungsdatum</Label>
                                <Input
                                    id="lic_original_issued"
                                    type="date"
                                    value={licenseForm.original_issued_date}
                                    onChange={(e) => setLicenseForm({ ...licenseForm, original_issued_date: e.target.value })}
                                    min="1900-01-01"
                                    max="2100-12-31"
                                    data-testid="license-original-issued-date"
                                />
                                <p className="text-xs text-muted-foreground">Datum der erstmaligen Lizenzerteilung (optional, wird bei Verlängerungen automatisch übernommen)</p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="lic_notes">Notizen</Label>
                                <Input
                                    id="lic_notes"
                                    value={licenseForm.notes}
                                    onChange={(e) => setLicenseForm({ ...licenseForm, notes: e.target.value })}
                                    placeholder="Optionale Anmerkungen..."
                                    data-testid="license-notes"
                                />
                            </div>

                            <div className="space-y-1">
                                <PdfDropzone
                                    file={licensePdfFile}
                                    onChange={setLicensePdfFile}
                                    label="Lizenz-PDF hochladen (optional)"
                                    hint="Datei hierhin ziehen oder klicken zum Auswählen."
                                    testId="license-pdf-dropzone"
                                />
                                <p className="text-xs text-muted-foreground pl-1">
                                    Wird nach dem Speichern automatisch in den Dropbox-Ordner des Akademie-Mitglieds (<code className="text-[10px] bg-gray-100 px-1 rounded">/BGPR/&lt;Name&gt;/Lizenzen/</code>) abgelegt.
                                </p>
                            </div>

                            {editingLicense && (
                                <TrainingProofsSection
                                    licenseId={editingLicense.id}
                                />
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setLicenseDialogOpen(false)}>Abbrechen</Button>
                            <Button type="submit" disabled={savingLicense} data-testid="save-license-button">
                                {savingLicense && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {editingLicense ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Deactivation Reason Dialog */}
            <Dialog open={deactivateDialogOpen} onOpenChange={(open) => { if (!open) { setDeactivateDialogOpen(false); setDeactivatingLicenseId(null); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Lizenz deaktivieren</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Bitte geben Sie den Grund für die Deaktivierung an:
                        </p>
                        <textarea
                            value={deactivationReason}
                            onChange={(e) => setDeactivationReason(e.target.value)}
                            placeholder="Grund für die Deaktivierung..."
                            className="w-full min-h-[80px] px-3 py-2 rounded-md border bg-background text-sm resize-none"
                            data-testid="deactivation-reason-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => { setDeactivateDialogOpen(false); setDeactivatingLicenseId(null); }}>
                            Abbrechen
                        </Button>
                        <Button variant="destructive" onClick={handleDeactivateLicense} data-testid="confirm-deactivate-button">
                            Deaktivieren
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Renew License Dialog — reiche Variante mit UE-Auswahl */}
            <Dialog open={renewDialogOpen} onOpenChange={(open) => { if (!open) { setRenewDialogOpen(false); setRenewingLicense(null); } }}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0">
                    <DialogHeader className="p-6 pb-3 border-b">
                        <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-blue-600" /> Lizenz verlängern</DialogTitle>
                    </DialogHeader>
                    {renewingLicense && (() => {
                        const lic = renewingLicense;
                        const cutoff = lic.issued_date || '';
                        const allRecords = fullProfile?.training_records || [];
                        const regular = allRecords.filter(r => r.record_type !== 'first_aid');
                        const firstAid = allRecords.filter(r => r.record_type === 'first_aid');
                        const totalSelectedUE = Object.entries(renewSelectedRecords)
                            .filter(([, v]) => v)
                            .reduce((sum, [id]) => {
                                const rec = regular.find(x => x.id === id);
                                return sum + (rec ? (rec.approved_ue != null ? rec.approved_ue : rec.ue_count || 0) : 0);
                            }, 0);
                        const baseYear = lic.valid_until ? parseInt(String(lic.valid_until).slice(0, 4), 10) : new Date().getFullYear();
                        return (
                        <form onSubmit={handleRenewLicense} className="flex flex-col flex-1 overflow-hidden">
                            <div className="flex-1 overflow-y-auto p-6 space-y-5">
                                <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-lg text-sm">
                                    <p className="font-medium text-blue-900">{lic.license_type} <span className="text-blue-700">· Nr. {lic.license_number}</span></p>
                                    <p className="text-xs text-blue-800/80 mt-1">
                                        Ausgestellt: {formatDateDE(lic.issued_date)} · Gültig bis: <span className="font-semibold">{formatDateDE(lic.valid_until)}</span>
                                    </p>
                                </div>

                                {/* Erste-Hilfe */}
                                <div>
                                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-2"><HeartPulse className="h-4 w-4 text-red-500" /> Erste-Hilfe-Nachweis (HLW)</h4>
                                    {firstAid.length > 0 ? firstAid.map(r => (
                                        <div key={r.id} className="flex items-center justify-between p-2 rounded border bg-red-50/50 text-sm">
                                            <span>{r.name} – {formatDateDE(r.date_issued)}, {r.ue_count} UE</span>
                                        </div>
                                    )) : <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">Kein Erste-Hilfe-Nachweis hinterlegt.</p>}
                                </div>

                                {/* Fortbildungsnachweise mit Checkboxen */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Fortbildungsnachweise</h4>
                                        <div className="text-xs text-gray-600">
                                            <span className="font-semibold">{totalSelectedUE} UE</span> ausgewählt
                                            {cutoff && <span className="ml-2 text-gray-400">(ab {formatDateDE(cutoff)} anrechenbar)</span>}
                                        </div>
                                    </div>
                                    {regular.length > 0 ? (
                                        <div className="space-y-1">
                                            {regular.map(r => {
                                                const isUsed = !!(r.used_for_renewal_id || r.used_for_license_id);
                                                const outOfWindow = cutoff && (r.date_issued || '') < cutoff;
                                                const isRejected = (r.review_status || 'submitted') === 'rejected';
                                                const isSelectable = !isUsed && !outOfWindow && !isRejected;
                                                const isSelected = !!renewSelectedRecords[r.id];
                                                const displayUE = r.approved_ue != null ? r.approved_ue : r.ue_count;
                                                return (
                                                    <div key={r.id} className={`flex items-center gap-3 p-2.5 rounded border text-sm ${isUsed ? 'bg-gray-100 border-gray-200 opacity-60' : outOfWindow ? 'bg-amber-50 border-amber-200' : isRejected ? 'bg-red-50 border-red-200' : isSelected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
                                                        {isSelectable ? (
                                                            <input type="checkbox" checked={isSelected}
                                                                onChange={e => setRenewSelectedRecords({ ...renewSelectedRecords, [r.id]: e.target.checked })}
                                                                className="w-4 h-4 shrink-0"
                                                                data-testid={`renew-consume-${r.id}`} />
                                                        ) : <span className="w-4 shrink-0" />}
                                                        <div className="flex-1">
                                                            <span className="font-medium">{r.name}</span> – {formatDateDE(r.date_issued)}, {displayUE} UE
                                                            {isUsed && <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Bereits verwendet</span>}
                                                            {outOfWindow && !isUsed && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Außerhalb des Fensters</span>}
                                                            {isRejected && <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Abgelehnt</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : <p className="text-sm text-gray-500">Keine Fortbildungsnachweise</p>}
                                </div>
                            </div>

                            {/* Entscheidung */}
                            <div className="p-6 border-t bg-gray-50 space-y-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-700">Verlängerungsdauer</label>
                                    <div className="flex gap-2 mt-1">
                                        <label className={`flex-1 border rounded-lg px-3 py-2 text-sm cursor-pointer ${renewYears === 2 ? 'bg-green-50 border-green-400 text-green-800 font-semibold' : 'bg-white border-gray-200'}`}>
                                            <input type="radio" className="mr-2" checked={renewYears === 2} onChange={() => {
                                                setRenewYears(2);
                                                setRenewForm(p => ({ ...p, valid_until: `${baseYear + 2}-12-31` }));
                                            }} data-testid="direct-renew-2-years" />
                                            2 Jahre (Standard)
                                        </label>
                                        <label className={`flex-1 border rounded-lg px-3 py-2 text-sm cursor-pointer ${renewYears === 1 ? 'bg-amber-50 border-amber-400 text-amber-800 font-semibold' : 'bg-white border-gray-200'}`}>
                                            <input type="radio" className="mr-2" checked={renewYears === 1} onChange={() => {
                                                setRenewYears(1);
                                                setRenewForm(p => ({ ...p, valid_until: `${baseYear + 1}-12-31` }));
                                            }} data-testid="direct-renew-1-year" />
                                            1 Jahr (Einzelfall)
                                        </label>
                                    </div>
                                </div>
                                {renewYears === 1 && (
                                    <div>
                                        <label className="text-xs font-medium text-amber-800">Begründung <span className="text-red-500">*</span></label>
                                        <textarea rows={2} value={renewExceptionNotes}
                                            onChange={e => setRenewExceptionNotes(e.target.value)}
                                            className="w-full border rounded px-2 py-1 text-sm mt-1"
                                            data-testid="direct-renew-exception-notes" />
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-medium text-gray-700">Gültig bis *</label>
                                        <Input type="date" value={renewForm.valid_until}
                                            onChange={e => setRenewForm({ ...renewForm, valid_until: e.target.value })}
                                            min="1900-01-01" max="2100-12-31"
                                            required data-testid="direct-renew-valid-until" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-700">Notiz (optional)</label>
                                        <Input value={renewForm.notes}
                                            onChange={e => setRenewForm({ ...renewForm, notes: e.target.value })}
                                            placeholder="z. B. Direkt durch Geschäftsstelle"
                                            data-testid="direct-renew-notes" />
                                    </div>
                                </div>
                                <p className="text-[11px] text-gray-500">
                                    Anzurechnende UE: <strong>{totalSelectedUE}</strong>. Nicht angehakte Nachweise bleiben frei für spätere Verlängerungen.
                                </p>
                            </div>
                            <DialogFooter className="p-4 border-t bg-white">
                                <Button type="button" variant="outline" onClick={() => { setRenewDialogOpen(false); setRenewingLicense(null); }}>Abbrechen</Button>
                                <Button type="submit" disabled={savingRenew} data-testid="confirm-renew-button">
                                    {savingRenew && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Verlängern
                                </Button>
                            </DialogFooter>
                        </form>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}

function InfoItem({ icon: Icon, label, value, isLink }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                <Icon className="h-3 w-3" /> {label}
            </p>
            {value ? (
                isLink ? (
                    <a href={isLink} className="text-sm font-medium text-primary hover:underline whitespace-pre-wrap">{value}</a>
                ) : (
                    <p className="text-sm font-medium whitespace-pre-wrap">{value}</p>
                )
            ) : (
                <p className="text-sm text-muted-foreground">-</p>
            )}
        </div>
    );
}

function SidebarItem({ label, value }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium">{value}</span>
        </div>
    );
}


function formatDateDE(d) {
    if (!d) return '-';
    const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    try {
        let date;
        if (d.includes('.') && !d.includes('-')) {
            const parts = d.split('.');
            date = new Date(parts[2], parts[1] - 1, parts[0]);
        } else {
            date = new Date(d);
        }
        return `${date.getDate()}. ${MONTHS_DE[date.getMonth()]} ${date.getFullYear()}`;
    } catch { return d; }
}

function getLicenseStatus(lic) {
    const isActive = lic.is_active !== false;
    const status = lic.status || (isActive ? 'active' : 'deactivated');

    if (status === 'renewed') return { label: 'Verlängert', class: 'bg-blue-100 text-blue-700', key: 'renewed' };
    if (!isActive || status === 'deactivated') return { label: 'Deaktiviert', class: 'bg-gray-100 text-gray-600', key: 'deactivated' };

    let validDate;
    try {
        validDate = lic.valid_until?.includes('.')
            ? new Date(lic.valid_until.split('.').reverse().join('-'))
            : new Date(lic.valid_until);
    } catch { validDate = new Date(); }
    // Defensive: if the stored date has an obviously wrong year (e.g. "0026-12-31"
    // because a 2-digit year was entered), don't silently flag the license as
    // expired — surface a clear „Datum prüfen"-state so the admin opens the
    // license and corrects it (or runs the admin „Datums-Heilung"-action).
    const year = validDate && !isNaN(validDate.getTime()) ? validDate.getFullYear() : null;
    if (year !== null && (year < 1900 || year > 2100)) {
        return { label: 'Datum prüfen', class: 'bg-amber-100 text-amber-800 border border-amber-300', key: 'invalid_date' };
    }
    const now = new Date();
    if (validDate < now) return { label: 'Abgelaufen', class: 'bg-red-100 text-red-700', key: 'expired' };
    if (validDate < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)) return { label: 'Läuft bald ab', class: 'bg-amber-100 text-amber-700', key: 'expiring' };
    return { label: 'Gültig', class: 'bg-green-100 text-green-700', key: 'valid' };
}

function LicenseList({ licenses, onEdit, onToggle, onRenew, onDownloadPdf, onDelete, onUndoRenewal, onVerify, showHistory, toggleHistory, btnColors, trainingRecords = [], renewals = [], renewalRules = null }) {
    // Filter out revoked licenses entirely — sie sind das Resultat einer
    // zurückgenommenen Verlängerung und sollen weder in der aktuellen Übersicht
    // noch in der Historie auftauchen. Der zugehörige Antrag wird via
    // "Lizenzverlängerungsanträge → Zurückgenommen / Erneut prüfen" gepflegt.
    const visibleLicenses = (licenses || []).filter(l => (l.status || 'active') !== 'revoked');
    // Group licenses by license_number
    const grouped = {};
    visibleLicenses.forEach(lic => {
        const num = lic.license_number;
        if (!grouped[num]) grouped[num] = [];
        grouped[num].push(lic);
    });

    // Sort each group: aktuellste Lizenz zuerst.
    // Priorität: (1) Status-Rang — active > extended > renewed > revoked/deactivated
    //            (2) valid_until DESC
    //            (3) created_at DESC
    // Damit rutscht eine frisch verlängerte Lizenz (status='active') immer an
    // die Spitze und alte Perioden (extended/renewed) wandern in die Historie.
    const statusRank = (lic) => {
        if (lic.is_active === false) return 4;
        const s = lic.status || 'active';
        if (s === 'active') return 0;
        if (s === 'extended') return 1;
        if (s === 'renewed') return 2;
        if (s === 'deactivated') return 3;
        return 5;
    };
    Object.keys(grouped).forEach(num => {
        grouped[num].sort((a, b) => {
            const ra = statusRank(a), rb = statusRank(b);
            if (ra !== rb) return ra - rb;
            const va = (a.valid_until || '').slice(0, 10);
            const vb = (b.valid_until || '').slice(0, 10);
            if (va !== vb) return vb.localeCompare(va);
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
    });

    // Sort the GROUPS so the group whose current license is most recently
    // valid (active first, then by valid_until DESC) appears at the top.
    // Without this, groups iterated in insertion order which surfaced the
    // historical (expired) license group above the freshly renewed one.
    const sortedGroups = Object.entries(grouped).sort(([, ga], [, gb]) => {
        const a = ga[0], b = gb[0];
        const ra = statusRank(a), rb = statusRank(b);
        if (ra !== rb) return ra - rb;
        const va = (a.valid_until || '').slice(0, 10);
        const vb = (b.valid_until || '').slice(0, 10);
        if (va !== vb) return vb.localeCompare(va);
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

    return (
        <div className="space-y-4">
            {sortedGroups.map(([licNumber, group]) => {
                const current = group[0];
                const history = group.slice(1);
                const hasHistory = history.length > 0;
                const historyOpen = showHistory[licNumber];

                return (
                    <div key={licNumber} className="space-y-1">
                        <LicenseCard
                            lic={current}
                            onEdit={onEdit}
                            onToggle={onToggle}
                            onRenew={onRenew}
                            onDownloadPdf={onDownloadPdf}
                            onDelete={onDelete}
                            onUndoRenewal={onUndoRenewal}
                            onVerify={onVerify}
                            isCurrent
                            btnColors={btnColors}
                            trainingRecords={trainingRecords}
                            renewals={renewals}
                            renewalRules={renewalRules}
                        />

                        {hasHistory && (
                            <button
                                onClick={() => toggleHistory(licNumber)}
                                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-4 py-1.5 transition"
                                data-testid={`toggle-history-${licNumber}`}
                            >
                                <History className="h-3 w-3" />
                                {historyOpen ? 'Historie ausblenden' : `Historie anzeigen (${history.length})`}
                                {historyOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </button>
                        )}

                        {historyOpen && history.map(lic => (
                            <LicenseCard
                                key={lic.id}
                                lic={lic}
                                onEdit={onEdit}
                                onToggle={onToggle}
                                onRenew={onRenew}
                                onDownloadPdf={onDownloadPdf}
                                onDelete={onDelete}
                                isCurrent={false}
                                btnColors={btnColors}
                                trainingRecords={trainingRecords}
                                renewals={renewals}
                                renewalRules={renewalRules}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function LicenseCard({ lic, onEdit, onToggle, onRenew, onDownloadPdf, onDelete, onUndoRenewal, onVerify, isCurrent, btnColors, trainingRecords = [], renewals = [], renewalRules = null }) {
    const status = getLicenseStatus(lic);
    const isActive = lic.is_active !== false && lic.status !== 'renewed';
    const isRenewed = lic.status === 'renewed';
    // Schutz: Wenn ein offener Verlängerungs-Antrag (pending) existiert,
    // wird der Direkt-„Verlängern"-Button ausgeblendet, damit der Admin nicht
    // versehentlich am Antragsprüfungs-Workflow vorbei verlängert.
    const hasPendingRenewal = Array.isArray(renewals) && renewals.some(r => r.status === 'pending');
    const canRenew = isActive && status.key !== 'renewed' && !hasPendingRenewal;
    // Nur Verlängerungs-Lizenzen (mit parent_license_id) können rückgängig
    // gemacht werden — Undo zeigt sich nur als sicherer Bereinigungs-Knopf.
    const canUndoRenewal = !!lic.parent_license_id && !isRenewed && onUndoRenewal;
    // Vom Mitglied selbst eingereichte Bestandslizenz, noch nicht anerkannt
    const needsVerification = lic.source === 'self_uploaded' && !lic.verified_by_admin && onVerify;

    // Phase 2a: Verfügbare UE für Verlängerung (nur bei ablaufenden/abgelaufenen Lizenzen,
    // die die aktuelle Periode sind). Historie bekommt keinen Badge.
    const showAvailableUeBadge = isCurrent && !isRenewed && isActive &&
        (status.key === 'expired' || status.key === 'expiring');
    let availableUeBadge = null;
    if (showAvailableUeBadge && Array.isArray(trainingRecords)) {
        const cutoff = lic.issued_date || '';
        const eligible = trainingRecords.filter(r =>
            r.record_type !== 'first_aid'
            && !r.used_for_renewal_id
            && (r.review_status || 'submitted') !== 'rejected'
            && (!cutoff || (r.date_issued || '') >= cutoff)
        );
        const availableUE = eligible.reduce((sum, r) =>
            sum + (r.approved_ue != null ? r.approved_ue : r.ue_count || 0), 0);

        // Erforderliche UE aus Rules ableiten
        let requiredUE = null;
        try {
            const rr = renewalRules || { rules: [], max_renewable_months: 36 };
            const vu = lic.valid_until?.includes('.')
                ? new Date(lic.valid_until.split('.').reverse().join('-'))
                : new Date(lic.valid_until);
            const months = (new Date() - vu) / (30.44 * 24 * 60 * 60 * 1000);
            const monthsSinceExpiry = Math.max(0, Math.round(months));
            const sortedRules = [...(rr.rules || [])].sort(
                (a, b) => (a.max_expired_months || 0) - (b.max_expired_months || 0));
            const matched = sortedRules.find(r => monthsSinceExpiry <= (r.max_expired_months || 0))
                || sortedRules[sortedRules.length - 1];
            if (matched && monthsSinceExpiry <= (rr.max_renewable_months || 36)) {
                requiredUE = matched.required_ue;
            }
        } catch {
            requiredUE = null;
        }

        let badgeClass = 'bg-red-100 text-red-700 border-red-200';
        let label;
        if (requiredUE == null) {
            label = `${availableUE} UE verfügbar`;
            badgeClass = availableUE > 0 ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-100 text-gray-600 border-gray-200';
        } else if (availableUE >= requiredUE && requiredUE > 0) {
            label = `${availableUE}/${requiredUE} UE verfügbar ✓`;
            badgeClass = 'bg-green-100 text-green-700 border-green-200';
        } else if (availableUE > 0) {
            label = `${availableUE}/${requiredUE} UE verfügbar`;
            badgeClass = 'bg-amber-100 text-amber-800 border-amber-200';
        } else {
            label = `0/${requiredUE} UE verfügbar`;
            badgeClass = 'bg-red-100 text-red-700 border-red-200';
        }
        availableUeBadge = (
            <span
                className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${badgeClass}`}
                title="Anrechenbare Fortbildungs-UE (ab Ausstellungsdatum dieser Lizenz)"
                data-testid={`license-available-ue-${lic.id}`}
            >
                {label} für Verlängerung
            </span>
        );
    }

    // Phase 2b: "UE-Basis"-Block für Lizenzen, die aus einer Verlängerung
    // entstanden sind — bevorzugt direkt am Lizenz-Doc, Fallback auf Renewal-Join.
    const sourcingRenewal = (renewals || []).find(r => r.created_license_id === lic.id);
    const consumedRecords = (Array.isArray(lic.consumed_training_records) && lic.consumed_training_records.length)
        ? lic.consumed_training_records
        : (sourcingRenewal?.consumed_training_records || []);
    const totalConsumedUE = lic.total_consumed_ue != null
        ? lic.total_consumed_ue
        : (sourcingRenewal?.total_consumed_ue != null
            ? sourcingRenewal.total_consumed_ue
            : consumedRecords.reduce((s, c) => s + (c.ue || 0), 0));

    return (
        <div className={`p-4 rounded-lg border ${isRenewed ? 'bg-blue-50/30 opacity-60' : !isActive ? 'bg-gray-50 opacity-60' : isCurrent ? 'bg-muted/30' : 'bg-muted/20'}`}
             data-testid={`license-card-${lic.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className={`font-medium text-sm ${isCurrent ? '' : 'text-muted-foreground'}`}>{lic.license_type}</p>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.class}`}>
                            {status.label}
                        </span>
                        {!isCurrent && (
                            <span className="text-xs text-muted-foreground">(Vorperiode)</span>
                        )}
                        {availableUeBadge}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Nr. {lic.license_number}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                        <span>Ausgestellt: {formatDateDE(lic.issued_date)}</span>
                        {lic.original_issued_date && <span>Erstausstellung: {formatDateDE(lic.original_issued_date)}</span>}
                        <span>Gültig bis: {formatDateDE(lic.valid_until)}</span>
                    </div>
                    {lic.notes && <p className="text-xs text-muted-foreground mt-1 italic">{lic.notes}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {!isRenewed && (
                        <>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onDownloadPdf(lic)}
                                title="PDF herunterladen"
                                data-testid={`download-pdf-${lic.id}`}
                            >
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => onEdit(lic)}
                                style={{ backgroundColor: btnColors.btn_edit, color: btnColors.btn_edit_text }}
                                className="hover:opacity-90"
                                data-testid={`edit-license-${lic.id}`}
                            >
                                Bearbeiten
                            </Button>
                            {canRenew && (
                                <Button
                                    size="sm"
                                    onClick={() => onRenew(lic)}
                                    style={{ backgroundColor: btnColors.btn_renew, color: btnColors.btn_renew_text }}
                                    className="hover:opacity-90"
                                    data-testid={`renew-license-${lic.id}`}
                                >
                                    Verlängern
                                </Button>
                            )}
                            {hasPendingRenewal && !isRenewed && (
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-amber-50 text-amber-800 border border-amber-200"
                                    title="Offener Lizenzverlängerungs-Antrag — bitte über die Antragsprüfung verlängern"
                                    data-testid={`pending-renewal-hint-${lic.id}`}
                                >
                                    Offener Antrag — über Prüfung verlängern
                                </span>
                            )}
                            {canUndoRenewal && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onUndoRenewal(lic)}
                                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                    title="Diese Verlängerung rückgängig machen (alte Lizenz reaktivieren, Fortbildungs-Nachweise freigeben)"
                                    data-testid={`undo-renewal-${lic.id}`}
                                >
                                    Verlängerung rückgängig
                                </Button>
                            )}
                            {needsVerification && (
                                <>
                                    <Button
                                        size="sm"
                                        onClick={() => onVerify(lic, false)}
                                        className="bg-green-600 hover:bg-green-700 text-white"
                                        title="Bestandslizenz nach Prüfung anerkennen — auch abgelaufene Lizenzen können anerkannt werden (Historie)"
                                        data-testid={`approve-license-${lic.id}`}
                                    >
                                        Anerkennen
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onVerify(lic, true)}
                                        className="text-red-700 border-red-300 hover:bg-red-50"
                                        title="Eingereichte Bestandslizenz ablehnen (mit Begründung an das Mitglied)"
                                        data-testid={`reject-license-${lic.id}`}
                                    >
                                        Ablehnen
                                    </Button>
                                </>
                            )}
                            <div className="ml-1" title={isActive ? 'Aktiv' : 'Deaktiviert'}>
                                <Switch
                                    checked={isActive}
                                    onCheckedChange={(checked) => onToggle(lic, checked)}
                                    data-testid={`toggle-license-${lic.id}`}
                                />
                            </div>
                            {onDelete && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onDelete(lic)}
                                    title="Lizenz löschen"
                                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                                    data-testid={`delete-license-${lic.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
            {!isActive && lic.deactivation_reason && (
                <p className="text-xs text-red-600 mt-2 border-t pt-2">Grund: {lic.deactivation_reason}</p>
            )}
            {consumedRecords.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200 bg-blue-50/50 -mx-4 -mb-4 px-4 pb-3 rounded-b-lg"
                     data-testid={`license-ue-basis-${lic.id}`}>
                    <p className="text-xs font-semibold text-blue-900 flex items-center gap-1.5 mb-2">
                        <Award className="h-3.5 w-3.5" />
                        UE-Basis für diese Verlängerung ({totalConsumedUE} UE gesamt)
                    </p>
                    <ul className="space-y-1">
                        {consumedRecords.map((c, idx) => (
                            <li key={c.id || idx} className="text-xs text-blue-900/90 flex items-center justify-between gap-2">
                                <span className="truncate">
                                    <span className="font-medium">{c.name || 'Nachweis'}</span>
                                    {c.date_issued && <span className="text-blue-900/60"> – {formatDateDE(c.date_issued)}</span>}
                                </span>
                                <span className="font-semibold shrink-0">{c.ue || 0} UE</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}


// ============ Training Record Row (editable by admin) ============
function TrainingRecordRow({ record: r, onReload }) {
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Reject dialog
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectNotes, setRejectNotes] = useState('');
    const [rejectOptions, setRejectOptions] = useState([]);
    const [rejectLoading, setRejectLoading] = useState(false);

    const isSystem = !!r.auto_created;
    const isUsed = !!(r.used_for_renewal_id || r.used_for_license_id);
    // Bug Teil 55/2: `canReview` durfte nicht auf `!isUsed` gestützt werden.
    // Fälschlich verbrauchte Records (Direkt-Verlängerung ohne Prüfung) müssen
    // dennoch von der Geschäftsstelle geprüft und ggf. korrigiert werden können.
    const canReview = !isSystem;
    const canUnconsume = !isSystem && isUsed;
    const reviewStatus = r.review_status || 'submitted';
    const needsAttention = isUsed && reviewStatus !== 'approved';

    const startEdit = () => {
        setEditData({
            name: r.name,
            date_issued: r.date_issued,
            ue_count: r.ue_count,
            approved_ue: r.approved_ue ?? r.ue_count,
        });
        setEditing(true);
    };

    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            // Metadata changes via adminEdit (name/date/ue_count do not trigger mails)
            await trainingApi.adminEdit(r.id, {
                name: editData.name,
                date_issued: editData.date_issued,
                ue_count: editData.ue_count,
            });
            toast.success('Nachweis aktualisiert');
            setEditing(false);
            onReload();
        } catch (e) { toast.error('Fehler beim Speichern'); }
        finally { setSaving(false); }
    };

    const handleQuickApprove = async () => {
        setActionLoading(true);
        try {
            // Full approval (approved_ue = ue_count → voll anerkannt Mail)
            await trainingApi.adminApprove(r.id, { approved_ue: r.ue_count, notify: true });
            toast.success('Nachweis anerkannt & Mitglied informiert');
            onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Anerkennen');
        } finally { setActionLoading(false); }
    };

    const handleApproveWithCustomUE = async () => {
        const approvedUe = parseInt(editData.approved_ue);
        if (isNaN(approvedUe) || approvedUe < 0) {
            toast.error('Bitte eine gültige UE-Anzahl angeben');
            return;
        }
        setActionLoading(true);
        try {
            await trainingApi.adminApprove(r.id, { approved_ue: approvedUe, notify: true });
            toast.success(approvedUe === r.ue_count ? 'Nachweis anerkannt' : `Nachweis teilweise anerkannt (${approvedUe} UE)`);
            setEditing(false);
            onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally { setActionLoading(false); }
    };

    const openRejectDialog = async () => {
        setRejectReason('');
        setRejectNotes('');
        setRejectOpen(true);
        try {
            const res = await settingsApi.getRejectionReasons();
            setRejectOptions(res.data.options || []);
        } catch { setRejectOptions([]); }
    };

    const handleRejectSubmit = async () => {
        if (!rejectReason) {
            toast.error('Bitte einen Ablehnungsgrund wählen');
            return;
        }
        setRejectLoading(true);
        try {
            await trainingApi.adminReject(r.id, {
                rejection_reason: rejectReason,
                admin_notes: rejectNotes,
                notify: true,
            });
            toast.success('Nachweis abgelehnt & Mitglied informiert');
            setRejectOpen(false);
            onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Ablehnen');
        } finally { setRejectLoading(false); }
    };

    const openDoc = () => {
        // Direktöffnen mit ?token=… — damit Browser den Content-Disposition
        // Filename respektiert (Blob-URLs ignorieren das und zeigen UUID-Namen).
        const token = localStorage.getItem('bgpr_token');
        const url = `${getBackendUrl()}/api/training-records/${r.id}/document?token=${encodeURIComponent(token)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Bei auto-erstellten Nachweisen (record_type aus BGPR-Workshop) existiert
    // KEINE hochgeladene PDF — die Lehrgangsbescheinigung wird beim Klick
    // on-demand über den Booking-Endpoint regeneriert.
    const downloadAutoCert = async () => {
        if (!r.booking_id) {
            toast.error('Keine Buchung verknüpft – PDF kann nicht regeneriert werden');
            return;
        }
        try {
            const res = await bookingApi.downloadCertificate(r.booking_id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            // Dateiname aus Content-Disposition extrahieren (sonst UUID)
            const cd = res.headers?.['content-disposition'] || '';
            const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
            const filename = m && m[1]
                ? decodeURIComponent(m[1])
                : `Bescheinigung_${r.booking_id.slice(0, 8)}.pdf`;
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bescheinigung konnte nicht geladen werden');
        }
    };

    const syncDropbox = async () => {
        try {
            await dropboxApi.syncTrainingRecord(r.id);
            toast.success('In Dropbox hochgeladen');
            onReload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Dropbox-Fehler'); }
    };

    const statusBadge = (s) => {
        if (isSystem) return <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Aus BGPR-Seminar</span>;
        if (s === 'approved') return <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Anerkannt</span>;
        if (s === 'rejected') return <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold">Abgelehnt</span>;
        return <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Zu prüfen</span>;
    };

    if (editing) {
        return (
            <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/30 space-y-2" data-testid={`edit-record-${r.id}`}>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-xs text-gray-500">Bezeichnung</label>
                        <input className="w-full border rounded px-2 py-1 text-sm" value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500">Datum</label>
                        <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={editData.date_issued} onChange={e => setEditData({ ...editData, date_issued: e.target.value })} />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500">Eingereichte UE</label>
                        <input type="number" className="w-full border rounded px-2 py-1 text-sm" value={editData.ue_count} onChange={e => setEditData({ ...editData, ue_count: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 font-semibold">Anerkannte UE</label>
                        <input type="number" className="w-full border rounded px-2 py-1 text-sm border-green-300 bg-green-50" value={editData.approved_ue} onChange={e => setEditData({ ...editData, approved_ue: parseInt(e.target.value) || 0 })} data-testid={`approved-ue-${r.id}`} />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" onClick={handleSaveEdit} disabled={saving || actionLoading} variant="outline" className="h-7 text-xs" data-testid={`save-record-${r.id}`}>
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />} Daten speichern
                    </Button>
                    {canReview && (
                        <Button size="sm" onClick={handleApproveWithCustomUE} disabled={saving || actionLoading} className="h-7 text-xs bg-green-600 hover:bg-green-700" data-testid={`approve-custom-${r.id}`}>
                            {actionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3 mr-1" />} Anerkennen ({editData.approved_ue} UE)
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs"><X className="h-3 w-3" /></Button>
                </div>
                {canReview && editData.approved_ue != editData.ue_count && (
                    <p className="text-[11px] text-amber-700">
                        Abweichende UE: Mitglied erhält die Mail „Teilweise anerkannt".
                    </p>
                )}
            </div>
        );
    }

    return (
        <>
        <div className={`flex items-center justify-between p-3 rounded-lg border ${reviewStatus === 'rejected' ? 'bg-red-50 border-red-200' : r.record_type === 'first_aid' ? 'bg-amber-50 border-amber-200' : isUsed ? 'bg-gray-100 border-gray-200' : 'bg-gray-50'}`} data-testid={`training-record-${r.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-900">{r.name}</p>
                    {r.record_type === 'first_aid' && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">1. Hilfe / HLW</span>}
                    {statusBadge(reviewStatus)}
                    {isUsed && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title={r.used_at ? `Für Verlängerung verbraucht am ${formatDateDE(r.used_at?.split('T')[0])}` : 'Für Verlängerung verbraucht'}>Verwendet{r.used_at ? ` (${formatDateDE(r.used_at.split('T')[0])})` : ''}</span>}
                </div>
                <p className="text-xs text-gray-500">
                    {formatDateDE(r.date_issued)} | Eingereicht: {r.ue_count} UE
                    {r.approved_ue != null && r.approved_ue !== r.ue_count && (
                        <span className="text-green-700 font-semibold ml-1">| Anerkannt: {r.approved_ue} UE</span>
                    )}
                    {r.original_filename && <span className="text-blue-600 ml-2">{r.original_filename}</span>}
                </p>
                {reviewStatus === 'rejected' && r.rejection_reason && (
                    <p className="text-xs text-red-700 mt-0.5"><strong>Grund:</strong> {r.rejection_reason}</p>
                )}
                {needsAttention && (
                    <p className="text-xs text-amber-800 mt-1 flex items-center gap-1" data-testid={`used-but-unreviewed-warning-${r.id}`}>
                        <AlertTriangle className="h-3 w-3" />
                        <span>Wurde für eine Lizenzverlängerung verbraucht, ist aber noch nicht anerkannt. Bitte prüfen oder Verwendung zurücksetzen.</span>
                    </p>
                )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                {r.document_path && (
                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50 h-8 text-xs" onClick={openDoc} data-testid={`view-document-${r.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                )}
                {!r.document_path && isSystem && r.booking_id && (
                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50 h-8 text-xs" onClick={downloadAutoCert} data-testid={`download-auto-cert-${r.id}`} title="Lehrgangsbescheinigung als PDF herunterladen (wird on-demand regeneriert)">
                        <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                )}
                {canReview && reviewStatus !== 'approved' && (
                    <Button variant="outline" size="sm" className="h-8 text-xs text-green-700 border-green-300 hover:bg-green-50" onClick={handleQuickApprove} disabled={actionLoading} data-testid={`approve-record-${r.id}`} title="Voll anerkennen & Mitglied informieren">
                        {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ThumbsUp className="h-3.5 w-3.5 mr-1" />Anerkennen</>}
                    </Button>
                )}
                {canReview && reviewStatus !== 'rejected' && (
                    <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={openRejectDialog} data-testid={`reject-record-${r.id}`} title="Nachweis ablehnen">
                        <ThumbsDown className="h-3.5 w-3.5 mr-1" />Ablehnen
                    </Button>
                )}
                {canReview && (
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={startEdit} data-testid={`edit-record-btn-${r.id}`} title="Bearbeiten / UE anpassen">
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                )}
                {canUnconsume && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                        onClick={async () => {
                            if (!window.confirm(`Verwendung dieses Nachweises zurücksetzen?\n\n„${r.name}" ist aktuell für eine Lizenzverlängerung verrechnet. Nach dem Zurücksetzen steht der Nachweis wieder als „${(r.review_status || 'submitted') === 'approved' ? 'anerkannt' : 'zu prüfen'}" zur Verfügung.\n\nDie bereits ausgestellte Folge-Lizenz bleibt bestehen — sie kann bei Bedarf separat über „Verlängerung rückgängig" zurückgerollt werden.`)) return;
                            try {
                                await trainingApi.adminUnconsume(r.id);
                                toast.success('Verwendung zurückgesetzt');
                                onReload && onReload();
                            } catch (e) {
                                toast.error(e.response?.data?.detail || 'Reset fehlgeschlagen');
                            }
                        }}
                        data-testid={`unconsume-record-${r.id}`}
                        title="Verwendungs-Marker entfernen (bei fälschlich verrechneten Nachweisen)"
                    >
                        <Undo2 className="h-3.5 w-3.5 mr-1" />Verwendung zurücksetzen
                    </Button>
                )}
                {r.document_path && !r.dropbox_path && (
                    <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-8 text-xs" onClick={syncDropbox} data-testid={`sync-dropbox-${r.id}`}>
                        <CloudUpload className="h-3.5 w-3.5" />
                    </Button>
                )}
                {r.dropbox_path && <span className="text-xs text-emerald-600"><CloudUpload className="h-3 w-3 inline" /></span>}
            </div>
        </div>

        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
            <DialogContent data-testid={`reject-dialog-${r.id}`}>
                <DialogHeader>
                    <DialogTitle>Fortbildungsnachweis ablehnen</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-gray-50 border text-xs text-gray-700">
                        <p className="font-semibold">{r.name}</p>
                        <p>{formatDateDE(r.date_issued)} · {r.ue_count} UE</p>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Ablehnungsgrund <span className="text-red-500">*</span></Label>
                        <Select value={rejectReason} onValueChange={setRejectReason}>
                            <SelectTrigger data-testid={`reject-reason-select-${r.id}`}><SelectValue placeholder="Grund wählen..." /></SelectTrigger>
                            <SelectContent>
                                {rejectOptions.length === 0 ? (
                                    <SelectItem value="__none" disabled>Keine Gründe konfiguriert</SelectItem>
                                ) : rejectOptions.map((opt) => (
                                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground">Liste pflegbar unter Einstellungen → Verlängerungsregeln.</p>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Zusätzliche Anmerkung (optional)</Label>
                        <Textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} rows={3} placeholder="Persönliche Ergänzung an das Mitglied" data-testid={`reject-notes-${r.id}`} />
                    </div>
                    <p className="text-[11px] text-amber-700">
                        Das Mitglied erhält automatisch eine E-Mail mit Grund und optionaler Anmerkung.
                    </p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setRejectOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleRejectSubmit} disabled={rejectLoading} className="bg-red-600 hover:bg-red-700" data-testid={`reject-submit-${r.id}`}>
                        {rejectLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ThumbsDown className="h-4 w-4 mr-1" />}
                        Ablehnen & Mail senden
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}


// ============ Renewal Requests Section (with review dialog) ============
function ExistingLicenseAdoptBox({ renewalId, data, onAdopted }) {
    const [form, setForm] = useState({
        license_number: data.license_number || '',
        issued_date: data.issued_date || '',
        valid_until: data.valid_until || '',
        license_type: 'Herzgruppenleiter',
    });
    const [loading, setLoading] = useState(false);
    const alreadyAdopted = !!data.adopted_license_id;

    const openPdf = async () => {
        try {
            const res = await fetch(
                `${getBackendUrl()}/api/license-renewals/admin/${renewalId}/existing-document`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('bgpr_token')}` } }
            );
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), '_blank');
        } catch { toast.error('Fehler beim Laden der PDF'); }
    };

    const adopt = async () => {
        if (!form.license_number || !form.issued_date || !form.valid_until) {
            toast.error('Lizenznummer, Ausstellungsdatum und Gültigkeit erforderlich');
            return;
        }
        setLoading(true);
        try {
            const { data: api } = await import('../../lib/api');
            await api.default.post(`/license-renewals/admin/${renewalId}/adopt-existing-license`, form);
            toast.success('Lizenz übernommen und verifiziert');
            onAdopted();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Übernehmen');
        } finally { setLoading(false); }
    };

    return (
        <div className="p-4 rounded-lg border-2 border-indigo-200 bg-indigo-50" data-testid="adopt-existing-license-box">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold flex items-center gap-2 text-indigo-900">
                    <Shield className="h-4 w-4" />
                    Vom Nutzer hochgeladene bestehende Lizenz
                </h4>
                {data.document_path && (
                    <Button variant="outline" size="sm" onClick={openPdf} className="h-7 text-xs" data-testid="adopt-view-pdf">
                        <Eye className="h-3 w-3 mr-1" /> PDF-Vorschau
                    </Button>
                )}
            </div>
            <p className="text-xs text-indigo-700 mb-3">
                Prüfen Sie die Angaben anhand der PDF. Übernehmen Sie die Daten mit einem Klick in eine neue Lizenz im System &ndash; sie wird automatisch als verifiziert markiert.
            </p>
            {alreadyAdopted ? (
                <div className="p-2 rounded bg-green-100 text-green-800 text-sm">Bereits übernommen &ndash; die Lizenz ist im System angelegt.</div>
            ) : (
                <div className="space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs text-gray-700">Lizenznummer</label>
                            <input className="w-full border rounded px-2 py-1 text-sm" value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} data-testid="adopt-license-number" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-700">Lizenztyp</label>
                            <input className="w-full border rounded px-2 py-1 text-sm" value={form.license_type} onChange={e => setForm({ ...form, license_type: e.target.value })} data-testid="adopt-license-type" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-700">Ausstellungsdatum</label>
                            <input type="date" min="1900-01-01" max="2100-12-31" className="w-full border rounded px-2 py-1 text-sm" value={form.issued_date} onChange={e => setForm({ ...form, issued_date: e.target.value })} data-testid="adopt-issued-date" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-700">Gültig bis</label>
                            <input type="date" min="1900-01-01" max="2100-12-31" className="w-full border rounded px-2 py-1 text-sm" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} data-testid="adopt-valid-until" />
                        </div>
                    </div>
                    <Button onClick={adopt} disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="adopt-license-btn">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                        Lizenz aus Antrag übernehmen
                    </Button>
                </div>
            )}
        </div>
    );
}

function RenewalRequestsSection({ renewals, onReload }) {
    const [reviewData, setReviewData] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [validUntil, setValidUntil] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const [editableRecords, setEditableRecords] = useState({});
    const [selectedRecords, setSelectedRecords] = useState({}); // { [recordId]: true/false }
    const [renewalYears, setRenewalYears] = useState(2);
    const [exceptionNotes, setExceptionNotes] = useState('');
    const [renewalRules, setRenewalRules] = useState(null);

    const openReview = async (renewalId) => {
        setReviewLoading(true);
        try {
            const [reviewRes, rulesRes] = await Promise.all([
                renewalApi.getReview(renewalId),
                settingsApi.getRenewalRulesPublic(),
            ]);
            setReviewData(reviewRes.data);
            setRenewalRules(rulesRes.data);

            const defYears = rulesRes.data.default_renewal_years || 2;
            setRenewalYears(defYears);
            setExceptionNotes('');
            // Bezugsjahr = letztes gültig-bis der bisherigen Lizenz (nicht heute),
            // damit bei einer Lizenz, die bis 31.12.2025 galt, die 2-Jahres-
            // Verlängerung bis 31.12.2027 läuft (nicht bis 31.12.2028).
            const latestLic = (reviewRes.data.licenses || [])[0];
            const baseYear = latestLic?.valid_until
                ? parseInt(String(latestLic.valid_until).slice(0, 4), 10)
                : new Date().getFullYear();
            const year = baseYear + defYears;
            setValidUntil(`${year}-12-31`);
            setAdminNotes('');

            const editable = {};
            const selected = {};
            const eligibleIds = new Set((reviewRes.data.eligible_records || []).map(r => r.id));
            for (const r of reviewRes.data.training_records || []) {
                editable[r.id] = r.approved_ue != null ? r.approved_ue : r.ue_count;
                // Standard: alle eligiblen Nachweise angehakt, first-aid/out-of-window/bereits verwendet NICHT.
                selected[r.id] = eligibleIds.has(r.id);
            }
            setEditableRecords(editable);
            setSelectedRecords(selected);
        } catch (e) { toast.error('Fehler beim Laden der Prüfungsdaten'); }
        finally { setReviewLoading(false); }
    };

    // Nur angehakte Nachweise zählen zur UE-Summe für diese Verlängerung.
    const consumedRecordIds = Object.entries(selectedRecords).filter(([, v]) => v).map(([k]) => k);
    const totalEditableUE = consumedRecordIds.reduce(
        (sum, id) => sum + (parseInt(editableRecords[id]) || 0), 0
    );

    const handleAction = async (status) => {
        if (status === 'approved' && !validUntil) {
            toast.error('Bitte Gültigkeitsdatum angeben');
            return;
        }
        if (status === 'approved' && renewalYears === 1 && !exceptionNotes.trim()) {
            toast.error('Bei 1-Jahres-Verlängerung bitte eine Begründung angeben.');
            return;
        }
        setActionLoading(true);
        try {
            if (status === 'approved') {
                for (const r of reviewData.training_records || []) {
                    const approvedUe = parseInt(editableRecords[r.id]) || 0;
                    if (approvedUe !== r.ue_count || r.approved_ue !== approvedUe) {
                        await trainingApi.adminEdit(r.id, { approved_ue: approvedUe, review_status: 'approved' });
                    }
                }
            }
            const payload = { status, valid_until: validUntil, admin_notes: adminNotes };
            if (status === 'approved') {
                payload.consumed_record_ids = consumedRecordIds;
                payload.renewal_years = renewalYears;
                if (exceptionNotes.trim()) payload.exception_notes = exceptionNotes.trim();
            }
            await renewalApi.updateStatus(reviewData.renewal.id, payload);
            toast.success(status === 'approved' ? 'Lizenzverlängerung genehmigt' : status === 'revoked' ? 'Genehmigung zurückgenommen' : 'Antrag abgelehnt');
            setReviewData(null);
            onReload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
        finally { setActionLoading(false); }
    };

    const openDoc = async (recordId) => {
        try {
            const res = await fetch(
                `${getBackendUrl()}/api/training-records/${recordId}/document`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('bgpr_token')}` } }
            );
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), '_blank');
        } catch { toast.error('Fehler'); }
    };

    return (
        <>
            <div className="rounded-lg border bg-white p-6" data-testid="renewals-section">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    Lizenzverlängerungsanträge
                </h2>
                <div className="space-y-3">
                    {renewals.map((r) => (
                        <div key={r.id} className={`p-4 rounded-lg border ${
                            r.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                            r.status === 'approved' ? 'bg-green-50 border-green-200' :
                            r.status === 'revoked' ? 'bg-gray-100 border-gray-300' :
                            'bg-red-50 border-red-200'
                        }`} data-testid={`renewal-item-${r.id}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <p className="text-sm font-medium text-gray-900">Antrag vom {formatDateDE(r.created_at?.split('T')[0])}</p>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        r.status === 'approved' ? 'bg-green-100 text-green-700' :
                                        r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                        r.status === 'revoked' ? 'bg-gray-200 text-gray-600' :
                                        'bg-amber-100 text-amber-700'
                                    }`}>
                                        {r.status === 'approved' ? 'Genehmigt' : r.status === 'rejected' ? 'Abgelehnt' : r.status === 'revoked' ? 'Zurückgenommen' : 'In Bearbeitung'}
                                    </span>
                                    {r.valid_until && <span className="text-xs text-gray-500">Gültig bis: {formatDateDE(r.valid_until)}</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    {r.status === 'pending' && (
                                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs" onClick={() => openReview(r.id)} disabled={reviewLoading} data-testid={`review-renewal-${r.id}`}>
                                            <Eye className="h-3.5 w-3.5 mr-1" /> Prüfen
                                        </Button>
                                    )}
                                    {r.status === 'approved' && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs"
                                            onClick={async () => {
                                                if (!window.confirm('Genehmigung wirklich zurücknehmen? Die ausgestellte Lizenz wird widerrufen.')) return;
                                                try {
                                                    await renewalApi.updateStatus(r.id, { status: 'revoked' });
                                                    toast.success('Genehmigung zurückgenommen');
                                                    onReload();
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || 'Fehler');
                                                }
                                            }}
                                            data-testid={`revoke-renewal-${r.id}`}
                                        >
                                            <Undo2 className="h-3.5 w-3.5 mr-1" /> Zurücknehmen
                                        </Button>
                                    )}
                                    {(r.status === 'revoked' || r.status === 'rejected') && (
                                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50 h-8 text-xs"
                                            onClick={async () => {
                                                try {
                                                    await renewalApi.updateStatus(r.id, { status: 'pending' });
                                                    toast.success('Antrag wieder zur Prüfung geöffnet');
                                                    onReload();
                                                } catch (e) { toast.error('Fehler'); }
                                            }}
                                            data-testid={`reopen-renewal-${r.id}`}
                                        >
                                            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Erneut prüfen
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {r.admin_notes && <p className="text-xs text-gray-500 mt-1">Anmerkung: {r.admin_notes}</p>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Review Dialog */}
            {reviewData && reviewData.user_profile && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl" data-testid="review-dialog">
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold flex items-center gap-2"><Shield className="h-5 w-5 text-blue-600" /> Lizenzverlängerung prüfen</h3>
                                <Button variant="ghost" size="sm" onClick={() => setReviewData(null)}><X className="h-5 w-5" /></Button>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{reviewData.user_profile.salutation} {reviewData.user_profile.first_name} {reviewData.user_profile.last_name}</p>
                        </div>

                        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
                            {/* Lizenz-Status & Anforderungen */}
                            {(() => {
                                const rr = renewalRules || { rules: [], max_renewable_months: 36, first_aid_max_age_years: 2 };
                                const latestLicense = (reviewData.licenses || [])[0];
                                const validUntilDate = latestLicense ? new Date(latestLicense.valid_until) : null;
                                const now = new Date();
                                const expiredMonths = validUntilDate ? Math.max(0, (now - validUntilDate) / (30.44 * 24 * 60 * 60 * 1000)) : 0;

                                // Find matching rule (sorted by max_expired_months)
                                const sortedRules = [...(rr.rules || [])].sort((a, b) => (a.max_expired_months || 0) - (b.max_expired_months || 0));
                                const matchedRule = sortedRules.find(rule => expiredMonths <= (rule.max_expired_months || 0));
                                const canRenew = expiredMonths <= (rr.max_renewable_months || 36);
                                const requiredUE = matchedRule ? matchedRule.required_ue : (canRenew ? 15 : null);

                                // 1. Hilfe
                                const faMaxAge = rr.first_aid_max_age_years || 2;
                                const referenceDate = validUntilDate || now;
                                const firstAidValid = reviewData.first_aid_records?.some(r => {
                                    if (!r.date_issued) return false;
                                    const faDate = new Date(r.date_issued);
                                    const cutoff = new Date(referenceDate);
                                    cutoff.setFullYear(cutoff.getFullYear() - faMaxAge);
                                    return faDate >= cutoff;
                                });

                                const availableRegular = reviewData.regular_records?.filter(r => !r.used_for_renewal_id) || [];
                                const regularUE = availableRegular.reduce((sum, r) => sum + (parseInt(editableRecords[r.id]) || 0), 0);

                                const expiredDisplay = expiredMonths > 0
                                    ? expiredMonths < 1 ? 'weniger als 1 Monat' : `${Math.round(expiredMonths)} Monate`
                                    : null;

                                return (
                                    <div className={`p-4 rounded-lg border ${!canRenew ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`} data-testid="requirements-box">
                                        <h4 className="text-sm font-semibold mb-2">Anforderungen</h4>
                                        {latestLicense ? (
                                            <p className="text-xs text-gray-600 mb-2">
                                                Letzte Lizenz gültig bis: <span className="font-semibold">{formatDateDE(latestLicense.valid_until)}</span>
                                                {expiredDisplay && <span className="text-red-600 ml-1">(seit {expiredDisplay} abgelaufen)</span>}
                                            </p>
                                        ) : (
                                            <p className="text-xs text-gray-500 mb-2">Keine bestehende Lizenz im System</p>
                                        )}
                                        {!canRenew ? (
                                            <p className="text-sm text-red-700 font-semibold">Lizenz ist seit mehr als {rr.max_renewable_months} Monaten abgelaufen und kann nicht mehr verlängert werden.</p>
                                        ) : (
                                            <div className="space-y-1.5">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span>1. Hilfe-Nachweis (max. {faMaxAge} Jahre alt):</span>
                                                    {reviewData.has_first_aid ? (
                                                        firstAidValid
                                                            ? <span className="text-green-700 font-semibold flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Gültig</span>
                                                            : <span className="text-red-600 font-semibold flex items-center gap-1"><Ban className="h-3.5 w-3.5" /> Zu alt</span>
                                                    ) : (
                                                        <span className="text-red-600 font-semibold flex items-center gap-1"><Ban className="h-3.5 w-3.5" /> Fehlt</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span>Fortbildungs-UE (mind. {requiredUE} UE{matchedRule ? ` – ${matchedRule.label}` : ''}):</span>
                                                    <span className={`font-semibold flex items-center gap-1 ${regularUE >= requiredUE ? 'text-green-700' : 'text-red-600'}`}>
                                                        {regularUE >= requiredUE ? <Check className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                                                        {regularUE} / {requiredUE} UE
                                                    </span>
                                                </div>
                                                {regularUE < requiredUE && !(rr.half_ue_one_year_extension && regularUE >= Math.ceil(requiredUE / 2)) && (
                                                    <div className="mt-2 p-3 bg-red-100 border-2 border-red-300 rounded-lg" data-testid="ue-insufficient-warning">
                                                        <p className="text-sm font-bold text-red-800 flex items-center gap-1.5">
                                                            <Ban className="h-4 w-4" /> UE-Anforderung nicht erfüllt
                                                        </p>
                                                        <p className="text-xs text-red-700 mt-1">
                                                            Es fehlen noch <strong>{requiredUE - regularUE} UE</strong> für die Verlängerung.
                                                            Der Antrag sollte <strong>abgelehnt</strong> werden. Das Mitglied muss zunächst
                                                            weitere Fortbildungsnachweise nachreichen und anschließend einen neuen Antrag stellen.
                                                        </p>
                                                    </div>
                                                )}
                                                {rr.half_ue_one_year_extension && regularUE >= Math.ceil(requiredUE / 2) && regularUE < requiredUE && (
                                                    <p className="text-xs text-amber-700 bg-amber-50 p-1.5 rounded mt-1">
                                                        Bei halber UE-Zahl ({Math.ceil(requiredUE / 2)} UE) ist eine Verlängerung um 1 Jahr möglich.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Erste-Hilfe-Nachweis */}
                            <div>
                                <h4 className="text-sm font-semibold flex items-center gap-2 mb-2"><HeartPulse className="h-4 w-4 text-amber-600" /> Erste-Hilfe-Nachweis (HLW)</h4>
                                {reviewData.has_first_aid ? (
                                    reviewData.first_aid_records.map(r => (
                                        <div key={r.id} className="flex items-center gap-3 p-2.5 bg-amber-50 rounded border border-amber-200 mb-1">
                                            <div className="flex-1 text-sm">
                                                <span className="font-medium">{r.name}</span> – {formatDateDE(r.date_issued)}, {r.ue_count} UE
                                            </div>
                                            {r.document_path ? (
                                                <Button variant="outline" size="sm" className="h-7 text-xs text-blue-600 border-blue-200 shrink-0" onClick={() => openDoc(r.id)} data-testid={`review-pdf-${r.id}`}>
                                                    <Eye className="h-3 w-3 mr-1" /> PDF ansehen
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-gray-400 shrink-0">Kein PDF</span>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-red-600 bg-red-50 p-2 rounded">Kein Erste-Hilfe-Nachweis vorhanden</p>
                                )}
                            </div>

                            {/* Fortbildungsnachweise */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Fortbildungsnachweise</h4>
                                    <div className="text-xs text-gray-600">
                                        <span className="font-semibold">{totalEditableUE} UE</span> ausgewählt
                                        {reviewData.eligibility_cutoff_date && (
                                            <span className="ml-2 text-gray-400">(ab {formatDateDE(reviewData.eligibility_cutoff_date)} anrechenbar)</span>
                                        )}
                                    </div>
                                </div>
                                {reviewData.regular_records.length > 0 ? (
                                    <div className="space-y-1">
                                        {reviewData.regular_records.map(r => {
                                            const isUsed = !!(r.used_for_renewal_id || r.used_for_license_id);
                                            const outOfWindow = reviewData.eligibility_cutoff_date && (r.date_issued || '') < reviewData.eligibility_cutoff_date;
                                            const isRejected = r.review_status === 'rejected';
                                            const isSelectable = !isUsed && !outOfWindow && !isRejected;
                                            const isSelected = !!selectedRecords[r.id];
                                            return (
                                            <div key={r.id} className={`flex items-center gap-3 p-2.5 rounded border ${isUsed ? 'bg-gray-100 border-gray-200 opacity-60' : outOfWindow ? 'bg-amber-50 border-amber-200' : isRejected ? 'bg-red-50 border-red-200' : isSelected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
                                                {isSelectable ? (
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => setSelectedRecords({ ...selectedRecords, [r.id]: e.target.checked })}
                                                        className="w-4 h-4 text-primary shrink-0"
                                                        data-testid={`review-consume-${r.id}`}
                                                        title="Zur UE-Basis dieser Verlängerung hinzufügen"
                                                    />
                                                ) : (
                                                    <span className="w-4 shrink-0" />
                                                )}
                                                <div className="flex-1 text-sm">
                                                    <span className="font-medium">{r.name}</span> – {formatDateDE(r.date_issued)}, gemeldet: {r.ue_count} UE
                                                    {isUsed && (
                                                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Bereits verwendet</span>
                                                    )}
                                                    {outOfWindow && !isUsed && (
                                                        <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Ausstellungsdatum vor der aktuellen Lizenz-Ausstellung">Außerhalb des Fensters</span>
                                                    )}
                                                    {isRejected && (
                                                        <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Abgelehnt</span>
                                                    )}
                                                </div>
                                                {!isUsed && !isRejected && (
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <label className="text-xs text-green-700 font-medium">Anerkannt:</label>
                                                        <input type="number" min="0" className="w-16 border border-green-300 bg-green-50 rounded px-2 py-1 text-sm text-center font-semibold"
                                                            value={editableRecords[r.id] ?? r.ue_count}
                                                            onChange={e => setEditableRecords({ ...editableRecords, [r.id]: parseInt(e.target.value) || 0 })}
                                                            data-testid={`review-ue-${r.id}`}
                                                        />
                                                    </div>
                                                )}
                                                {r.document_path ? (
                                                    <Button variant="outline" size="sm" className="h-7 text-xs text-blue-600 border-blue-200 shrink-0" onClick={() => openDoc(r.id)} data-testid={`review-pdf-${r.id}`}>
                                                        <Eye className="h-3 w-3 mr-1" /> PDF
                                                    </Button>
                                                ) : (
                                                    <span className="text-xs text-gray-400 shrink-0">Kein PDF</span>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">Keine Fortbildungsnachweise</p>
                                )}
                            </div>

                            {/* Vom Nutzer hochgeladene bestehende Lizenz (nur bei Erstantrag ohne Lizenz im System) */}
                            {reviewData.existing_license_upload && (
                                <ExistingLicenseAdoptBox
                                    renewalId={reviewData.renewal.id}
                                    data={reviewData.existing_license_upload}
                                    onAdopted={() => openReview(reviewData.renewal.id)}
                                />
                            )}

                            {/* Bestehende Lizenzen */}
                            {reviewData.licenses?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold mb-2">Bestehende Lizenzen</h4>
                                    {reviewData.licenses.map(l => (
                                        <div key={l.id} className="text-xs text-gray-600 p-2 bg-gray-50 rounded mb-1 flex items-center justify-between gap-2">
                                            <div>
                                                {l.license_type_name || l.type || l.license_type} | Nr. {l.license_number} | Gültig bis: {formatDateDE(l.valid_until)}
                                                <span className={`ml-2 px-1.5 py-0.5 rounded ${l.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>{l.status}</span>
                                                {l.source === 'self_uploaded' && !l.verified_by_admin && (
                                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">In Prüfung</span>
                                                )}
                                                {l.source === 'adopted_from_renewal' && (
                                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Aus Antrag übernommen</span>
                                                )}
                                            </div>
                                            {l.document_path && (
                                                <Button
                                                    variant="outline" size="sm" className="h-7 text-xs"
                                                    onClick={async () => {
                                                        try {
                                                            const res = await fetch(`${getBackendUrl()}/api/licenses/${l.id}/document`, { headers: { Authorization: `Bearer ${localStorage.getItem('bgpr_token')}` } });
                                                            const blob = await res.blob();
                                                            window.open(URL.createObjectURL(blob), '_blank');
                                                        } catch { toast.error('Fehler beim Laden'); }
                                                    }}
                                                >
                                                    <Eye className="h-3 w-3 mr-1" /> PDF
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Entscheidung */}
                        <div className="p-6 border-t bg-gray-50 rounded-b-2xl space-y-3">
                            <div>
                                <label className="text-xs font-medium text-gray-700">Verlängerungsdauer</label>
                                <div className="flex gap-2 mt-1">
                                    <label className={`flex-1 border rounded-lg px-3 py-2 text-sm cursor-pointer ${renewalYears === 2 ? 'bg-green-50 border-green-400 text-green-800 font-semibold' : 'bg-white border-gray-200'}`}>
                                        <input type="radio" className="mr-2" checked={renewalYears === 2} onChange={() => {
                                            setRenewalYears(2);
                                            const baseLic = (reviewData?.licenses || [])[0];
                                            const baseYear = baseLic?.valid_until
                                                ? parseInt(String(baseLic.valid_until).slice(0, 4), 10)
                                                : new Date().getFullYear();
                                            setValidUntil(`${baseYear + 2}-12-31`);
                                        }} data-testid="renewal-2-years" />
                                        2 Jahre (Standard)
                                    </label>
                                    <label className={`flex-1 border rounded-lg px-3 py-2 text-sm cursor-pointer ${renewalYears === 1 ? 'bg-amber-50 border-amber-400 text-amber-800 font-semibold' : 'bg-white border-gray-200'}`}>
                                        <input type="radio" className="mr-2" checked={renewalYears === 1} onChange={() => {
                                            setRenewalYears(1);
                                            const baseLic = (reviewData?.licenses || [])[0];
                                            const baseYear = baseLic?.valid_until
                                                ? parseInt(String(baseLic.valid_until).slice(0, 4), 10)
                                                : new Date().getFullYear();
                                            setValidUntil(`${baseYear + 1}-12-31`);
                                        }} data-testid="renewal-1-year" />
                                        1 Jahr (Einzelfall-Ausnahme)
                                    </label>
                                </div>
                            </div>
                            {renewalYears === 1 && (
                                <div>
                                    <label className="text-xs font-medium text-amber-800">Begründung für 1-Jahres-Ausnahme <span className="text-red-500">*</span></label>
                                    <textarea
                                        rows={2}
                                        className="w-full border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 text-sm mt-1"
                                        value={exceptionNotes}
                                        onChange={(e) => setExceptionNotes(e.target.value)}
                                        placeholder="z.B. Fehlende UE werden im Laufe des Jahres nachgereicht."
                                        data-testid="renewal-exception-notes"
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-700">Gültig bis (Pflicht bei Genehmigung)</label>
                                    <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={validUntil} onChange={e => setValidUntil(e.target.value)} data-testid="renewal-valid-until" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700">Anmerkung (optional)</label>
                                    <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Interne Notiz" data-testid="renewal-admin-notes" />
                                </div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                                <span>Anzurechnende UE: <strong className="text-gray-900">{totalEditableUE}</strong> aus {consumedRecordIds.length} Nachweisen</span>
                                <span className="text-gray-400">Nicht angehakte Nachweise bleiben frei für spätere Verlängerungen.</span>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setReviewData(null)}>Abbrechen</Button>
                                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleAction('rejected')} disabled={actionLoading} data-testid="reject-renewal-btn">
                                    <Ban className="h-4 w-4 mr-1" /> Ablehnen
                                </Button>
                                <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleAction('approved')} disabled={actionLoading} data-testid="approve-renewal-btn">
                                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Genehmigen
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}


/**
 * MemberBookingRow — eine Zeile pro Seminar-Buchung im globalen Mitglied-Profil.
 * Zeigt zusätzlich bei attended=true zwei Buttons:
 *   • Bescheinigung herunterladen (regeneriert PDF on-demand)
 *   • Bescheinigung per Mail senden (Admin-Confirm + Versand-Endpoint)
 *
 * Hintergrund (05/2026): Admin sah „Bescheinigung liegt vor", konnte sie aber
 * weder ansehen noch erneut versenden. Diese Komponente fixt genau das im
 * globalen Mitglied-Detail (/admin/instructors/{id}).
 */
function MemberBookingRow({ booking: b, onReload }) {
    const [downloading, setDownloading] = useState(false);
    const [mailing, setMailing] = useState(false);
    const isAttended = !!b.attended;
    const isExternalCancelled = b.status === 'cancelled';

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await bookingApi.downloadCertificate(b.id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            // Backend liefert via Content-Disposition einen sprechenden Dateinamen
            // ('Bescheinigung_Nachname_Vorname_Kursnummer.pdf'). Den extrahieren
            // wir und fallen auf eine generische Variante zurück, falls der Header
            // mal fehlt.
            const cd = res.headers?.['content-disposition'] || '';
            const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
            const filename = match
                ? decodeURIComponent(match[1].trim())
                : `Bescheinigung_${b.id.slice(0, 8)}.pdf`;
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            // Reload, damit certificate_generated-Flag aktualisiert wird.
            if (onReload) onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bescheinigung konnte nicht geladen werden');
        } finally {
            setDownloading(false);
        }
    };

    const handleSendMail = async () => {
        const ok = window.confirm('Möchten Sie dem Mitglied die Bescheinigung erneut per E-Mail zusenden?');
        if (!ok) return;
        setMailing(true);
        try {
            const res = await bookingApi.sendCertificateByEmail(b.id);
            toast.success(res.data?.message || 'Bescheinigung versendet');
            if (onReload) onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
        } finally {
            setMailing(false);
        }
    };

    return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border gap-3 flex-wrap" data-testid={`member-booking-${b.id}`}>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{b.seminar_title}</p>
                <p className="text-xs text-gray-500">
                    {b.seminar_dates?.length > 0
                        ? b.seminar_dates.map(d => formatDateDE(d.date)).join(', ')
                        : 'Kein Datum'}
                </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {isAttended && !isExternalCancelled && (
                    <>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDownload}
                            disabled={downloading || mailing}
                            data-testid={`member-cert-download-${b.id}`}
                            title="Bescheinigung herunterladen (wird bei Bedarf neu erzeugt)"
                        >
                            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                            Bescheinigung
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleSendMail}
                            disabled={downloading || mailing}
                            data-testid={`member-cert-mail-${b.id}`}
                            title="Bescheinigung per E-Mail an dieses Mitglied senden"
                        >
                            {mailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                            Per Mail senden
                        </Button>
                    </>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    b.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    b.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                }`}>
                    {b.status === 'confirmed' ? 'Bestätigt' : b.status === 'cancelled' ? 'Storniert' : 'Ausstehend'}
                </span>
            </div>
        </div>
    );
}

// ============ Ausbildungsnachweise (Training Proofs) pro Lizenz ============
function TrainingProofsSection({ licenseId }) {
    const [proofs, setProofs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [fileToUpload, setFileToUpload] = useState(null);
    const [noteText, setNoteText] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await licenseApi.listProofs(licenseId);
            setProofs(res.data || []);
        } catch (e) {
            toast.error(await extractApiError(e, 'Nachweise konnten nicht geladen werden'));
        } finally {
            setLoading(false);
        }
    }, [licenseId]);

    useEffect(() => { reload(); }, [reload]);

    const handleUpload = async () => {
        if (!fileToUpload) return;
        if (!fileToUpload.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Nur PDF-Dateien erlaubt');
            return;
        }
        if (fileToUpload.size > 10 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 10 MB)');
            return;
        }
        setUploading(true);
        try {
            await licenseApi.uploadProof(licenseId, fileToUpload, noteText);
            toast.success('Ausbildungsnachweis hochgeladen');
            setFileToUpload(null);
            setNoteText('');
            reload();
        } catch (e) {
            toast.error(await extractApiError(e, 'Upload fehlgeschlagen'));
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (p) => {
        try {
            const res = await licenseApi.downloadProof(licenseId, p.id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = p.filename || `Nachweis_${p.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            toast.error(await extractApiError(e, 'Download fehlgeschlagen'));
        }
    };

    const handleDelete = async (p) => {
        if (!window.confirm(`Ausbildungsnachweis „${p.filename}" wirklich entfernen?`)) return;
        try {
            await licenseApi.deleteProof(licenseId, p.id);
            toast.success('Nachweis entfernt');
            reload();
        } catch (e) {
            toast.error(await extractApiError(e, 'Löschen fehlgeschlagen'));
        }
    };

    return (
        <div className="border-t pt-4 space-y-3" data-testid="training-proofs-section">
            <div>
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                    <FileText className="h-4 w-4 text-blue-700" />
                    Ausbildungsnachweise des Verbandes
                </Label>
                <p className="text-xs text-muted-foreground">
                    PDF-Nachweise (z. B. Ausbildungsbescheinigung des ausstellenden Verbandes). Werden dem Mitglied auch im Portal in dieser Lizenz-Kachel angezeigt.
                </p>
            </div>

            {loading ? (
                <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : proofs.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Noch keine Nachweise hinterlegt.</p>
            ) : (
                <ul className="space-y-1.5" data-testid="training-proofs-list">
                    {proofs.map((p) => (
                        <li key={p.id} className="flex items-center gap-2 p-2 bg-blue-50/50 rounded border border-blue-100 text-xs">
                            <FileText className="h-3.5 w-3.5 text-blue-700 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{p.filename}</p>
                                <p className="text-[10px] text-muted-foreground">
                                    {p.uploaded_at && new Date(p.uploaded_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    {p.uploaded_by && ` · ${p.uploaded_by}`}
                                    {p.size && ` · ${(p.size / 1024).toFixed(0)} KB`}
                                </p>
                                {p.note && <p className="text-[10px] italic text-blue-800 mt-0.5">{p.note}</p>}
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDownload(p)} className="h-7 px-2" data-testid={`download-proof-${p.id}`} title="Herunterladen">
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(p)} className="h-7 px-2 text-red-600 hover:text-red-700" data-testid={`delete-proof-${p.id}`} title="Entfernen">
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            <div className="pt-2 space-y-2">
                <PdfDropzone
                    file={fileToUpload}
                    onChange={setFileToUpload}
                    label="Neuen Ausbildungsnachweis hochladen (PDF)"
                    hint="Datei hierhin ziehen oder klicken zum Auswählen (max. 10 MB)."
                    testId="proof-dropzone"
                />
                {fileToUpload && (
                    <>
                        <Input
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Notiz zu diesem Nachweis (optional, z. B. Aussteller)"
                            className="text-sm"
                            data-testid="proof-note-input"
                        />
                        <Button type="button" size="sm" onClick={handleUpload} disabled={uploading} className="w-full" data-testid="upload-proof-button">
                            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />}
                            {uploading ? 'Wird hochgeladen...' : 'Nachweis speichern'}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

